/**
 * ARCHITECTURAL DRESSING — turning a structural box into a constructed thing.
 *
 * Every map in this game is a pile of axis-aligned boxes. Chamfering their
 * edges and painting a facade texture on them got the *surfaces* to stop
 * looking like Minecraft, but the SHAPES were still cuboids: a wall met the
 * ground with a knife edge, met the sky with a knife edge, and had no vertical
 * articulation anywhere in between. This module adds the mouldings a real
 * building has — plinth, pilasters, belt courses, window-bay mullions, cornice,
 * roof parapet, and an entrance — as extra static boxes.
 *
 * Split out of `renderer.ts` (2026-08-01) for one reason: inside `buildMap` it
 * needed a WebGLRenderer and a live scene to run, so none of the geometry rules
 * below could be checked offline. It is pure arithmetic and belongs on its own.
 * `agent/bench-architecture.mjs` is what that bought.
 *
 * COST MODEL. Everything emitted here is merged into the per-cell static detail
 * mesh, so a structure costs TRIANGLES and map-build time and never a draw call
 * or a frame of work. `dressStructure` emits a whole structure or nothing (the
 * caller decides by budget), which is deliberate: a half-dressed building —
 * cornice but no plinth — looks worse than an undressed one.
 *
 * COLLISION CONTRACT — read this before adding a piece.
 *  - Anything protruding OUTWARD of the collider is capped at ~0.25 m. That is
 *    small enough not to read as a ledge a player should be able to stand on.
 *  - Anything TALL (parapet, door jambs) sits INSIDE the box footprint, so it
 *    never invents a surface outside the collider.
 *  - The ONE deliberate exception is the parapet's height above the roof; see
 *    its comment. `bench-architecture` asserts all three of these.
 */

/** One emitted moulding: full extents + centre, in world space. */
export interface DressBox {
  w: number; h: number; d: number;
  x: number; y: number; z: number;
}

/** The subset of a MapBox this module needs. */
export interface DressTarget {
  p: [number, number, number];
  s: [number, number, number];
}

/** Largest first. The detail budget is finite and a landmark tower deserves it
 *  more than a 4 m crate does; volume is the cheapest decent proxy for "how
 *  much of the screen does this occupy". Dressing in map-declaration order —
 *  what shipped before 2026-08-01 — spent the budget on an arbitrary handful of
 *  boxes and left the landmarks bare. */
export function dressPriority(a: DressTarget, b: DressTarget): number {
  return (b.s[0] * b.s[1] * b.s[2]) - (a.s[0] * a.s[1] * a.s[2]);
}

/** Does this box get dressed at all? Trim, rails and rivets do not — mouldings
 *  on a 1 m box are noise, and the budget is better spent elsewhere. */
export function dressable(w: number, h: number, d: number): boolean {
  return h >= 3.2 && Math.max(w, d) >= 4.5;
}

/**
 * Does this box get the FACADE pieces (mullions, pilasters, parapet, entrance)?
 *
 * 2026-08-01, ground pass. The caller's `building` flag also drives the lit-
 * window material, so it demands `min(w,d) >= 2.5` — an occupiable volume. That
 * left every map built from THIN SLABS with nothing but a cornice and a plinth:
 * neon_district's towers are 1–2 m thick walls and gained exactly zero boxes
 * from the building pass (152 -> 152, the honest gap recorded that day). A wall
 * seven metres tall and seven wide is a facade to everyone looking at it,
 * whatever its thickness, so the DRESSING gate is separated from the MATERIAL
 * gate here. Nothing about the emissive windows changes.
 */
export function facadeLike(w: number, h: number, d: number): boolean {
  return h >= 7 && Math.max(w, d) >= 7 && Math.min(w, d) >= 0.9;
}

/**
 * Is this flat-ground slab worth a kerb? It has to be big enough that a 0.55 m
 * edge band is a detail rather than the whole thing, and roughly plaza-shaped —
 * a 40 x 2 m catwalk gets nothing.
 */
export function groundDressable(w: number, h: number, d: number): boolean {
  return h <= 1.2 && Math.min(w, d) >= 7 && Math.max(w, d) >= 12;
}

/** The MAXIMUM boxes `dressGround` can emit for one slab (one per side). */
export const GROUND_MAX_BOXES = 4;

/** Sides of a slab's top face, in the order `exposedGroundEdges` returns them. */
export const GROUND_SIDES = ["-x", "+x", "-z", "+z"] as const;

/**
 * Which sides of `slab`'s top face are OPEN — i.e. not butted against another
 * ground slab at the same level.
 *
 * This is the whole reason ground dressing is a two-argument function. Maps
 * here tile their floors out of several boxes (downtown's streets, the
 * compound's bays, every XL map), so kerbing all four sides of every slab would
 * draw a raised grid straight across the middle of a continuous plaza — worse
 * than no kerb at all. A side counts as covered when neighbouring slabs whose
 * tops are within 0.35 m of this one span at least 60% of that edge.
 */
export function exposedGroundEdges(slab: DressTarget, others: readonly DressTarget[]): boolean[] {
  const [w, h, d] = slab.s;
  const cx = slab.p[0], cz = slab.p[2], top = slab.p[1] + h / 2;
  const x0 = cx - w / 2, x1 = cx + w / 2, z0 = cz - d / 2, z1 = cz + d / 2;
  const EPS = 0.35;
  // covered fraction per side, accumulated as a 1-D span union sampled coarsely
  // (32 buckets is finer than the 0.55 m kerb it decides about, and avoids an
  // interval-merge for a handful of boxes)
  const N = 32;
  const hit = [new Uint8Array(N), new Uint8Array(N), new Uint8Array(N), new Uint8Array(N)];
  for (const o of others) {
    if (o === slab) continue;
    const [ow, oh, od] = o.s;
    if (Math.abs((o.p[1] + oh / 2) - top) > EPS) continue;
    const ox0 = o.p[0] - ow / 2, ox1 = o.p[0] + ow / 2;
    const oz0 = o.p[2] - od / 2, oz1 = o.p[2] + od / 2;
    // -x / +x sides: neighbour must reach across the face and overlap in z
    if (ox1 > x0 - EPS && ox0 < x0 + EPS) mark(hit[0], oz0, oz1, z0, z1, N);
    if (ox0 < x1 + EPS && ox1 > x1 - EPS) mark(hit[1], oz0, oz1, z0, z1, N);
    if (oz1 > z0 - EPS && oz0 < z0 + EPS) mark(hit[2], ox0, ox1, x0, x1, N);
    if (oz0 < z1 + EPS && oz1 > z1 - EPS) mark(hit[3], ox0, ox1, x0, x1, N);
  }
  return hit.map((b) => {
    let n = 0;
    for (let i = 0; i < N; i++) n += b[i];
    return n / N < 0.6;
  });
}

function mark(bucket: Uint8Array, a0: number, a1: number, lo: number, hi: number, n: number): void {
  const span = hi - lo;
  if (span <= 0) return;
  const i0 = Math.max(0, Math.floor(((a0 - lo) / span) * n));
  const i1 = Math.min(n - 1, Math.ceil(((a1 - lo) / span) * n) - 1);
  for (let i = i0; i <= i1; i++) bucket[i] = 1;
}

/**
 * Is this ground box NATURAL (grass, dirt, sand) rather than MADE (street,
 * plaza, deck, hangar floor)? Decided from the map's own box colour, so it
 * needs no per-map table and works for maps that do not exist yet.
 *
 * Why it matters: every flat-ground box in the game used to wear the `terrain`
 * texture — grass tufts and pebbles — including downtown's streets and every
 * arena deck. The largest surface on screen was the wrong material. Green and
 * earthy boxes keep `terrain`; everything else gets `paving`.
 *
 * Greys are the interesting case and they fall out of the saturation test:
 * a street is `#3a3f4a`-ish (sat ~0.11 but hue is blue, not green) and lands on
 * paving; grass is `#4d7a3a` (sat ~0.34, hue 96) and stays natural.
 */
export function naturalGround(colour: string | undefined): boolean {
  if (!colour) return false;
  const m = /^#?([0-9a-f]{6})$/i.exec(colour.trim());
  if (!m) return false;
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return false; // pure grey — a made surface
  const sat = l > 0.5 ? (max - min) / (2 - max - min) : (max - min) / (max + min);
  let hue: number;
  if (max === r) hue = ((g - b) / (max - min)) % 6;
  else if (max === g) hue = (b - r) / (max - min) + 2;
  else hue = (r - g) / (max - min) + 4;
  hue = ((hue * 60) + 360) % 360;
  if (hue >= 70 && hue <= 175) return sat >= 0.10;  // grass / moss / jungle
  if (hue >= 18 && hue < 70) return sat >= 0.20;    // dirt / sand / mesa
  return false;                                      // blues, greys, magentas
}

/**
 * KERBS. Emit a raised edge band along each OPEN side of a flat ground slab.
 *
 * The floor is the largest surface on screen in every map and it was the last
 * one with no geometry on it at all: a plaza ended in a knife edge and a street
 * met a building with nothing between them. A kerb is the single piece that
 * makes ground read as laid rather than as a plane — it gives the edge a
 * shadow line, a silhouette from any height, and a hard stop for the eye.
 *
 * COLLISION CONTRACT: the band is INSET, entirely inside the slab footprint, so
 * it never invents ground outside the collider. It does stand 0.16 m above the
 * walking surface — the same documented exception the parapet takes, at a third
 * the height. A real kerb is ~0.15 m; below that it is invisible, above that
 * you would expect to step up. `bench-architecture` asserts both.
 */
export function dressGround(slab: DressTarget, exposed: readonly boolean[], out: DressBox[]): void {
  const [w, h, d] = slab.s;
  const x0 = slab.p[0], z0 = slab.p[2];
  const top = slab.p[1] + h / 2;
  const kh = 0.16;
  const kw = Math.min(0.6, Math.max(0.34, Math.min(w, d) * 0.03));
  const y = top + kh / 2;
  if (exposed[0]) out.push({ w: kw, h: kh, d, x: x0 - w / 2 + kw / 2, y, z: z0 });
  if (exposed[1]) out.push({ w: kw, h: kh, d, x: x0 + w / 2 - kw / 2, y, z: z0 });
  if (exposed[2]) out.push({ w: w - kw * 2, h: kh, d: kw, x: x0, y, z: z0 - d / 2 + kw / 2 });
  if (exposed[3]) out.push({ w: w - kw * 2, h: kh, d: kw, x: x0, y, z: z0 + d / 2 - kw / 2 });
}

// ---------------------------------------------------------------------------
// STREET SURFACE (2026-08-01, `graphics`) — markings and utility furniture.
//
// The kerb gave a plaza an EDGE; the middle of it was still an untouched tiled
// plane, and the floor is the largest surface in every screenshot this game
// has ever produced. A road with no lane paint and no drains does not read as a
// road at any texture resolution — the paint is what tells you which way the
// street runs, how wide a lane is, and therefore how big everything else is.
//
// Both emitters are PURE (append-only, deterministic, no allocation beyond the
// pushed records) and live here rather than in `renderer.ts` for the same
// reason the mouldings do: `agent/bench-architecture.mjs` can then check the
// rules against every shipped map offline.
// ---------------------------------------------------------------------------

/**
 * Does this map's fiction have painted roads and cast-iron drain covers?
 *
 * The generic passes in this file work off SHAPE, which is what lets a map that
 * does not exist yet get dressed for free — but lane paint is the one piece
 * that is about PERIOD, not shape. A castle bailey and a frontier main street
 * are both long made-ground slabs, and both would have come out with a yellow
 * centre line and a manhole cover down the middle of them. The renderer already
 * carries the same idea for wild_west (`rustic`, which suppresses the office-
 * window facade); this is that list, stated once, where the rules live.
 *
 * Default is TRUE, so the free-for-a-new-map property survives: only a map that
 * declares itself historic opts out.
 */
const HISTORIC_MAPS = new Set(["medieval_castle", "wild_west_train_pursuit", "pirate_ship"]);
export function paintedStreets(mapId: string | undefined): boolean {
  return !mapId || !HISTORIC_MAPS.has(mapId);
}

/** One painted rectangle on the ground. `alongX` says which axis the stripe's
 *  LENGTH runs down, which is what the caller needs to orient the paint's UVs
 *  (the texture tiles along the stripe and is feathered across it). */
export interface StreetMark {
  x: number; z: number; y: number;
  /** full extents; the stripe's length is `w` when `alongX`, else `d` */
  w: number; d: number;
  alongX: boolean;
  /** warm = the yellow centre line; the rest of the paint is white */
  warm: boolean;
}

/** One piece of street ironwork. `alongX` orients a grate's slots (they always
 *  run parallel to the kerb they sit against); manholes ignore it. */
export interface StreetFixture {
  kind: "manhole" | "grate";
  x: number; z: number; y: number;
  alongX: boolean;
}

/** Per-slab caps. Both are the bound the caller uses to decide all-or-nothing,
 *  exactly like DRESS_MAX_BOXES. */
export const STREET_MAX_MARKS = 46;
export const STREET_MAX_FIXTURES = 8;

/** Paint sits 2 cm proud of the surface — a real thermoplastic line is about
 *  that, and it is enough to keep the quad off the floor's depth values without
 *  reading as a floating tile at a grazing angle. */
export const PAINT_LIFT = 0.02;
/** Width of a painted line on a slab whose SHORT span is `short`.
 *  A real lane line is 0.10–0.15 m; this game's world reads chunky and a
 *  0.15 m stripe on a 24 m carriageway is a hair at the range you fight at, so
 *  the line widens with the road and stops at 0.26 m. */
function paintWidth(short: number): number {
  return Math.min(0.26, Math.max(0.16, short * 0.014));
}

/**
 * MARKINGS for one made-ground slab.
 *
 * The slab's own proportions decide what it is: 1.6:1 or longer is a STREET
 * (centre dashes down the long axis, a solid edge line inside each long kerb,
 * and a zebra crossing near each end); anything squarer is a PLAZA and gets a
 * marked-out border instead. Nothing is invented that the shape does not
 * justify — a 12x12 arena deck with a centre line down it would be nonsense.
 *
 * `exposed` (from `exposedGroundEdges`) is respected for every edge-hugging
 * line: maps here tile their floors out of several slabs, and painting an edge
 * line down a seam between two touching slabs draws a stripe up the middle of
 * a continuous road.
 */
export function streetMarkings(
  slab: DressTarget, exposed: readonly boolean[], out: StreetMark[],
): void {
  const [w, h, d] = slab.s;
  const cx = slab.p[0], cz = slab.p[2];
  const y = slab.p[1] + h / 2 + PAINT_LIFT;
  const alongX = w >= d;
  const long = alongX ? w : d, short = alongX ? d : w;
  // clear of the kerb (0.34–0.6 wide) with room for a gutter, and never more
  // than a sixth of a narrow slab
  const inset = Math.min(1.5, Math.max(0.75, short * 0.06));
  const cap = out.length + STREET_MAX_MARKS;
  const PAINT_W = paintWidth(short);
  /** push one stripe given (along, across) offsets from the slab centre */
  const put = (
    alongOff: number, acrossOff: number, len: number, wide: number, warm: boolean,
  ): void => {
    if (out.length >= cap || len <= 0.2 || wide <= 0.02) return;
    out.push({
      x: cx + (alongX ? alongOff : acrossOff),
      z: cz + (alongX ? acrossOff : alongOff),
      y,
      w: alongX ? len : wide,
      d: alongX ? wide : len,
      alongX, warm,
    });
  };
  // which `exposed` slots are the two LONG sides of this slab?
  const loSide = alongX ? 2 : 0, hiSide = alongX ? 3 : 1;

  // A STREET is long, and no wider than a street: `short <= 26`. Without the
  // width cap neo_island's 420 x 102 m ground plate came out 4:1 and got a
  // dashed centre line painted 400 m down the middle of the island — the shape
  // test alone cannot tell a carriageway from a continent. The same argument
  // caps the plaza border at 120 m: past that the slab is the map's GROUND, not
  // a place with an edge.
  if (long / short >= 1.6 && long >= 12 && short <= 26) {
    // CENTRE LINE — dashed, 2.6 m of paint every 5.6 m. Drawn as separate
    // quads rather than one striped texture so the dash length is a world
    // measurement and stays the same on a 20 m street and a 90 m one.
    const period = 5.6, dash = 2.6;
    const n = Math.max(1, Math.floor((long - 2.4) / period));
    const span = n * period - (period - dash);
    for (let i = 0; i < n; i++) {
      put(-span / 2 + dash / 2 + i * period, 0, dash, PAINT_W, true);
    }
    // EDGE LINES — solid, just inside each open kerb.
    const edgeLen = long - inset * 2;
    if (exposed[loSide]) put(0, -(short / 2 - inset), edgeLen, PAINT_W, false);
    if (exposed[hiSide]) put(0, short / 2 - inset, edgeLen, PAINT_W, false);
    // ZEBRA CROSSINGS near each end. Bars run ALONG the road and repeat across
    // it — the orientation people get wrong, and the one that makes a crossing
    // instantly legible from a rooftop.
    if (long >= 26) {
      const barLen = 2.4, barW = 0.5, gap = 0.52;
      const acrossSpan = short - inset * 2;
      const bars = Math.min(9, Math.floor(acrossSpan / (barW + gap)));
      if (bars >= 3) {
        const step = acrossSpan / bars;
        for (const at of [-long * 0.36, long * 0.36]) {
          for (let i = 0; i < bars; i++) {
            put(at, -acrossSpan / 2 + step * (i + 0.5), barLen, barW, false);
          }
        }
      }
    }
  } else if (Math.min(w, d) >= 9 && long <= 120) {
    // PLAZA — a marked-out border, one line per open side, mitred short of the
    // corners so the four lines never overlap into a double-thick blob.
    const bi = inset * 1.7;
    if (exposed[loSide]) put(0, -(short / 2 - bi), long - bi * 2, PAINT_W, false);
    if (exposed[hiSide]) put(0, short / 2 - bi, long - bi * 2, PAINT_W, false);
    // ...and the two SHORT sides, which are stripes running the other way
    const sLo = alongX ? 0 : 2, sHi = alongX ? 1 : 3;
    for (const [side, sign] of [[sLo, -1], [sHi, 1]] as const) {
      if (!exposed[side] || out.length >= cap) continue;
      out.push({
        x: cx + (alongX ? sign * (long / 2 - bi) : 0),
        z: cz + (alongX ? 0 : sign * (long / 2 - bi)),
        y,
        w: alongX ? PAINT_W : short - bi * 2,
        d: alongX ? short - bi * 2 : PAINT_W,
        alongX: !alongX, warm: false,
      });
    }
  }
}

/**
 * DRAINS AND MANHOLES for one made-ground slab.
 *
 * Ironwork is the cheapest thing that says "there is a city under this floor".
 * Manholes sit off the centre line (nobody casts a cover through fresh paint);
 * grates hug the inside of an OPEN kerb, which is the only place a gutter can
 * be. Both are tiny and flush — see the caller for the 6 cm standing height,
 * which is under the kerb's already-documented 0.16 m exception.
 */
export function streetFixtures(
  slab: DressTarget, exposed: readonly boolean[], out: StreetFixture[],
): void {
  const [w, h, d] = slab.s;
  const cx = slab.p[0], cz = slab.p[2];
  const y = slab.p[1] + h / 2;
  const alongX = w >= d;
  const long = alongX ? w : d, short = alongX ? d : w;
  // same 120 m guard as the paint: past that the slab is the map's ground
  // plate, and two manholes lost in the middle of it are litter, not detail
  if (long > 120) return;
  const cap = out.length + STREET_MAX_FIXTURES;
  const put = (kind: "manhole" | "grate", alongOff: number, acrossOff: number, ax: boolean): void => {
    if (out.length >= cap) return;
    out.push({
      kind,
      x: cx + (alongX ? alongOff : acrossOff),
      z: cz + (alongX ? acrossOff : alongOff),
      y, alongX: ax,
    });
  };
  const off = short * 0.24;
  if (long >= 18) {
    put("manhole", -long * 0.22, -off, alongX);
    put("manhole", long * 0.24, off, alongX);
  } else {
    put("manhole", 0, off, alongX);
  }
  // gutter grates: against each open long kerb, a third and two thirds along
  const loSide = alongX ? 2 : 0, hiSide = alongX ? 3 : 1;
  const gAcross = short / 2 - 0.78;
  if (gAcross > 0.6 && long >= 14) {
    for (const [side, sign] of [[loSide, -1], [hiSide, 1]] as const) {
      if (!exposed[side]) continue;
      put("grate", -long * 0.28, sign * gAcross, alongX);
      put("grate", long * 0.3, sign * gAcross, alongX);
    }
  }
}

/** The MAXIMUM number of boxes `dressStructure` can emit for one target. The
 *  caller uses this to decide whether a structure fits in the remaining budget
 *  before it starts, which is what makes emission all-or-nothing. */
export const DRESS_MAX_BOXES = 56;

/**
 * Emit every moulding for one structure into `out`.
 *
 * `building` is the caller's classification (tall, deep enough to be an
 * occupiable building rather than a wall or a platform) — the facade-specific
 * pieces are gated on it.
 */
/** Which face of a structure is its FRONT — the one pointing back toward the
 *  middle of the map, which every map here is laid out around. The entrance
 *  surround, the awning and the shop sign all have to agree on this or the
 *  building ends up with a canopy over a blank wall. */
export function entranceFace(
  x0: number, z0: number, w = 1, d = 1,
): { faceX: boolean; sign: number } {
  // SHAPE FIRST (2026-08-05). Position alone had the front door of a 18 x 3 m
  // building on one of its 3 m ENDS whenever the building happened to sit
  // further out in x than in z — and most of this game's "buildings" are long
  // thin walls, so that was the common case, not the corner case. A slab's
  // front is its BROAD face; only a roughly-square plan is ambiguous enough for
  // the map's layout to decide.
  const faceX = w >= d * 2.5 ? false : d >= w * 2.5 ? true : Math.abs(x0) > Math.abs(z0);
  return { faceX, sign: faceX ? -Math.sign(x0 || 1) : -Math.sign(z0 || 1) };
}

export function dressStructure(box: DressTarget, building: boolean, out: DressBox[], woodFrame = false): void {
  const [w, h, d] = box.s;
  const x0 = box.p[0], y0 = box.p[1], z0 = box.p[2];
  const trim = Math.min(0.18, Math.max(0.075, Math.min(w, d) * 0.035));
  const put = (bw: number, bh: number, bd: number, x: number, y: number, z: number): void => {
    out.push({ w: bw, h: bh, d: bd, x, y, z });
  };

  // CORNICE — the cap where the wall meets the sky.
  const capH = Math.min(0.24, Math.max(0.08, h * 0.035));
  const yTop = y0 + h / 2 + capH * 0.12;
  put(w + trim * 2, capH, trim, x0, yTop, z0 - d / 2 - trim * 0.25);
  put(w + trim * 2, capH, trim, x0, yTop, z0 + d / 2 + trim * 0.25);
  put(trim, capH, d + trim * 2, x0 - w / 2 - trim * 0.25, yTop, z0);
  put(trim, capH, d + trim * 2, x0 + w / 2 + trim * 0.25, yTop, z0);

  // CORNER PILASTERS — the vertical stop at each corner of a tall facade.
  if (building && h >= 6) {
    const pilH = h * 0.82;
    const py = y0 - h * 0.04;
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
      put(trim, pilH, trim, x0 + sx * (w / 2 + trim * 0.2), py, z0 + sz * (d / 2 + trim * 0.2));
    }
  }

  // CORNER QUOINS (2026-08-26) — the raised render/stone blocks that articulate
  // a building's vertical corners. The tall facades above get a thin pilaster
  // stop; EVERY OTHER dressable wall (short buildings, arena walls, the plain
  // painted-plaster houses that used to wear metal rivets) sprang from its
  // corners with a knife edge, which is most of why they still read as extruded
  // cuboids. A proud L-quoin down each corner is the strongest "this was BUILT"
  // cue available at any range, and it is what turns the new plaster wall into
  // a plaster BUILDING. Emitted as an L per corner — one leg on each face — so
  // it reads from either approach. Projection is clamped WELL under the 0.26 m
  // ledge allowance, and the height stops short of the roof, so it takes no
  // collision exception (bench-architecture asserts the contract holds).
  if (!(building && h >= 6)) {
    const qW = Math.min(0.6, Math.max(0.3, Math.min(w, d) * 0.22)); // block width along the face
    const qProj = Math.min(0.16, Math.max(0.08, trim * 1.1));       // proud of the face
    const qH = h * 0.9;
    const qy = y0 - h * 0.02;
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
      // leg on the ±X face (thin in x, qW deep in z), inset so its z stays in footprint
      put(qProj, qH, qW, x0 + sx * (w / 2 + qProj * 0.5), qy, z0 + sz * (d / 2 - qW / 2));
      // leg on the ±Z face (qW wide in x, thin in z)
      put(qW, qH, qProj, x0 + sx * (w / 2 - qW / 2), qy, z0 + sz * (d / 2 + qProj * 0.5));
    }
  }

  // BASE PLINTH (2026-07-31) — every structure sprang straight out of the
  // ground with a knife edge, which is most of why they read as boxes dropped
  // on a plane rather than buildings standing on one. A short, slightly-wider
  // skirt gives the base a shadow line and a ground contact.
  {
    const plH = Math.min(0.55, Math.max(0.14, h * 0.05));
    const yBot = y0 - h / 2 + plH / 2;
    const over = trim * 0.8;
    put(w + over * 2, plH, over, x0, yBot, z0 - d / 2 - over * 0.4);
    put(w + over * 2, plH, over, x0, yBot, z0 + d / 2 + over * 0.4);
    put(over, plH, d + over * 2, x0 - w / 2 - over * 0.4, yBot, z0);
    put(over, plH, d + over * 2, x0 + w / 2 + over * 0.4, yBot, z0);
  }

  // TIMBER GIRT RAILS (2026-08-26, `graphics`) — on the two TIMBER maps only
  // (the ship's hull, the frontier town), a wooden building is a FRAME: sill,
  // girt, plate. The corner quoins above already read as the wood-material
  // corner POSTS; these horizontal rails are the girts that span between them,
  // and together they turn a wall of boards into a framed timber wall instead of
  // a flat plank decal. One girt at mid-height, a second higher up on tall
  // walls. Same thin-band-hugging-the-face shape as the plinth (projection
  // `trim*0.8`, far under the 0.26 m ledge contract), so it takes no collision
  // exception — bench-architecture asserts the whole wood-frame path stays clean.
  if (woodFrame && !(building && h >= 6)) {
    const railH = Math.min(0.22, Math.max(0.1, h * 0.03));
    const over = trim * 0.8;
    const fracs = h >= 4.5 ? [0.42, 0.74] : [0.5];
    for (const fr of fracs) {
      const ry = y0 - h / 2 + h * fr;
      // stay clear of the plinth skirt below and the cornice cap above
      if (ry < y0 - h / 2 + 0.7 || ry > y0 + h / 2 - 0.5) continue;
      put(w + over * 2, railH, over, x0, ry, z0 - d / 2 - over * 0.4);
      put(w + over * 2, railH, over, x0, ry, z0 + d / 2 + over * 0.4);
      put(over, railH, d + over * 2, x0 - w / 2 - over * 0.4, ry, z0);
      put(over, railH, d + over * 2, x0 + w / 2 + over * 0.4, ry, z0);
    }
  }

  // BELT COURSE (2026-07-31) — a horizontal string band every ~5 m up a tall
  // facade. Real buildings have floor lines; without them a 20 m wall is one
  // unbroken plane and the eye cannot judge its height at all.
  if (building && h >= 9) {
    const bandH = Math.min(0.2, Math.max(0.08, h * 0.012));
    const over = trim * 0.55;
    // capped at 8 floors: a 60 m Skyhaven tower would otherwise emit 24 belt
    // boxes on its own and blow DRESS_MAX_BOXES, which is the bound the caller
    // relies on to keep emission all-or-nothing (bench-architecture asserts it)
    for (let i = 0, by = y0 - h / 2 + 5; i < 8 && by < y0 + h / 2 - 1.5; i++, by += 5) {
      put(w + over * 2, bandH, over, x0, by, z0 - d / 2 - over * 0.4);
      put(w + over * 2, bandH, over, x0, by, z0 + d / 2 + over * 0.4);
    }
  }

  // ------------------------------------------------------------------------
  // 2026-08-01 — the three pieces below are the building pass's geometry.
  // ------------------------------------------------------------------------

  // WINDOW BAYS. The belt course gave facades a horizontal rhythm and nothing
  // gave them a vertical one, so a 20 m wall was a stack of stripes. Mullion
  // fins divide each face into bays roughly a window wide — the strongest
  // "this is a building, not a cuboid" cue at the distance a player actually
  // sees it from — and they line the geometry up with the window grid the
  // facade TEXTURE has been drawing all along.
  //
  // Sizing is not arbitrary: at `trim` width/projection (0.18 / 0.15 m) the
  // first capture of this pass could not tell the two columns apart at 25 m.
  // A real pilaster strip on a 12 m facade is ~0.5 m wide standing ~0.25 m
  // proud, and that is what casts the shadow that divides the wall.
  if (building && h >= 6) {
    const finH = h * 0.78;
    const fy = y0 - h * 0.03;
    const finW = Math.max(0.42, trim * 3);
    // The fin's projection IS its sideways protrusion past the collider wall,
    // so it is clamped to the contract's 0.26 m ledge threshold. `trim * 1.6`
    // alone reached 0.288 m on a wide facade — a 0.29 m shelf hanging outside
    // the collider at the fin's top. bench-architecture caught it; the visual
    // difference between 0.26 and 0.29 is nil.
    const over = Math.min(0.26, Math.max(0.24, trim * 1.6));
    for (const axisX of [true, false]) {
      const len = axisX ? w : d;
      // one fin per ~3.2 m of face (a window bay), capped at 4 so a very long
      // wall does not eat a small map's whole budget
      const n = Math.min(4, Math.floor(len / 3.2) - 1);
      for (let i = 1; i <= n; i++) {
        const t = (i / (n + 1) - 0.5) * len;
        if (axisX) {
          put(finW, finH, over, x0 + t, fy, z0 - d / 2 - over * 0.5);
          put(finW, finH, over, x0 + t, fy, z0 + d / 2 + over * 0.5);
        } else {
          put(over, finH, finW, x0 - w / 2 - over * 0.5, fy, z0 + t);
          put(over, finH, finW, x0 + w / 2 + over * 0.5, fy, z0 + t);
        }
      }
    }
  }

  // ROOF PARAPET. The one piece here that changes the SKYLINE: every roof in
  // the game was a clean horizontal cut against the sky, which is exactly what
  // a cube looks like and what no real building looks like. A low upstand wall,
  // with the cornice reading as its coping, fixes the silhouette at any range.
  //
  // THE DELIBERATE COLLISION EXCEPTION: this stands ABOVE the collider top, so
  // on a walkable roof you can walk through it. It is capped at 0.42 m — below
  // the height you would hesitate at — and its footprint is INSET, so it never
  // widens the roof. Do not raise the cap without making it a real collider.
  if (building && h >= 6 && Math.min(w, d) >= 3.5) {
    const upH = Math.min(0.42, Math.max(0.26, h * 0.04));
    const uy = y0 + h / 2 + upH / 2;
    const thick = Math.min(0.4, Math.max(0.16, Math.min(w, d) * 0.05));
    put(w, upH, thick, x0, uy, z0 - d / 2 + thick / 2);
    put(w, upH, thick, x0, uy, z0 + d / 2 - thick / 2);
    put(thick, upH, d - thick * 2, x0 - w / 2 + thick / 2, uy, z0);
    put(thick, upH, d - thick * 2, x0 + w / 2 - thick / 2, uy, z0);
  }

  // ENTRANCE SURROUND. A building with no way in is scenery; two jambs and a
  // lintel at eye level is what tells a player standing at ground level that
  // the thing in front of them is architecture. Placed on the face pointing
  // back toward the middle of the map (every map here is laid out around the
  // origin), which is the face a player approaches from — and sunk INSIDE the
  // footprint so it never invents a step.
  // `min(w,d) >= 2.2` (2026-08-01, ground pass): with thin slab towers now
  // dressed as facades, a 1 m thick wall would emit a 0.55 m wide "door" —
  // which reads as a random rivet, not an entrance.
  if (building && h >= 6 && Math.min(w, d) >= 2.2) {
    const doorW = Math.min(3.2, Math.min(w, d) * 0.55);
    const doorH = Math.min(3.4, h * 0.42);
    const jamb = Math.max(0.22, doorW * 0.13);
    const depth = Math.max(0.24, trim * 1.6);
    const { faceX, sign } = entranceFace(x0, z0, w, d);
    const dy = y0 - h / 2 + doorH / 2;
    const ly = y0 - h / 2 + doorH;
    if (faceX) {
      const fx = x0 + sign * (w / 2 - depth / 2);
      put(depth, doorH, jamb, fx, dy, z0 - doorW / 2);
      put(depth, doorH, jamb, fx, dy, z0 + doorW / 2);
      put(depth, jamb, doorW + jamb, fx, ly, z0);
    } else {
      const fz = z0 + sign * (d / 2 - depth / 2);
      put(jamb, doorH, depth, x0 - doorW / 2, dy, fz);
      put(jamb, doorH, depth, x0 + doorW / 2, dy, fz);
      put(doorW + jamb, jamb, depth, x0, ly, fz);
    }
  }
}

/* ===========================================================================
 * THE ROOFLINE (2026-08-01, mission `graphics`).
 *
 * Two problems, one module section.
 *
 * 1. EVERY ROOF IN THE GAME WORE THE WALL'S TEXTURE. A map box is one merged
 *    family material for all six faces, so a `building` box's TOP face was
 *    painted with the office-window `facade` tile: a grid of lit windows lying
 *    flat, facing the sky. This game has jetpacks, ziplines, helicopters, a
 *    flying saucer and three rooftop maps, so that is not a hidden face.
 *    `roofPieces` lays a DECK — a 7 cm ballast slab wearing the new `roofing`
 *    membrane — over the roof of every dressed structure.
 *
 * 2. EVERY ROOF WAS EMPTY. The parapet (see `dressStructure`) gave the skyline
 *    an edge; nothing gave it anything to stand against. Real roofs carry
 *    plant: condenser units, a water tank on legs, vent stacks, a stair hut.
 *    That furniture is what turns a flat-topped extrusion into a building seen
 *    from above, and it is the strongest silhouette cue available on every map
 *    with a skyline.
 *
 * COLLISION CONTRACT — this section's is stricter than the rest of the file,
 * because plant is TALL and the client cannot add colliders (the AABBs are
 * server-side, shared/maps.ts).
 *  - The DECK is 7 cm. That is the kerb/paint class of exception: you cannot
 *    trip on it and it cannot hide anything.
 *  - FURNITURE is capped at `ROOF_PLANT_MAX_H` above the roof and is confined
 *    to a PERIMETER BAND inside the parapet — the central `ROOF_CLEAR` of the
 *    roof is left completely empty. On a roof a player can actually reach, the
 *    middle is where they land, walk and fight; the band is where they would
 *    already be blocked by the parapet. `bench-architecture` asserts both.
 *  - `roofFurniture(mapId)` opts the three ROOFTOP maps out of furniture
 *    entirely (they fight up there — the whole playfield is roofs). They keep
 *    the deck, which is pure upside: their floor stops being window glass.
 * ========================================================================= */

/** One piece of roof furniture. The caller turns `kind` into geometry and picks
 *  the material — deck → `roofing`, the open machinery (`plant`, `fan`) →
 *  `louvre`, everything else → `galv`. */
export interface RoofPiece {
  kind: "deck" | "plant" | "hut" | "tank" | "leg" | "stack"
    /* ROUND TWO (2026-08-05) */ | "post" | "rail" | "hatch" | "fan";
  /** full extents; for `tank`/`stack`/`fan` (cylinders) `w` is the DIAMETER and `d` is ignored */
  w: number; h: number; d: number;
  x: number; y: number; z: number;
}

/** Bound the caller uses for all-or-nothing emission, like DRESS_MAX_BOXES.
 *  Raised 14 → 52 by ROUND TWO: the perimeter railing alone is up to 8 rails
 *  and 20 posts. They are all 12-triangle boxes merged into an existing mesh,
 *  so this costs triangles at map-load, never draw calls or frame time. */
export const ROOF_MAX_PIECES = 52;
/** Deck thickness, and how far its top stands above the collider's roof. */
export const ROOF_DECK_H = 0.07;
/** Tallest any furniture may stand above the roof surface. */
export const ROOF_PLANT_MAX_H = 2.6;
/** Fraction of the roof (per axis) kept clear of furniture, centred. */
export const ROOF_CLEAR = 0.5;

/* --- ROUND TWO (2026-08-05, mission `graphics`) ---------------------------
 *
 * The roofline pass gave every roof a membrane and a corner of plant. What it
 * left is what this adds:
 *
 *  - A RAILING on top of the parapet. This is the piece that changes the
 *    SKYLINE at range: a parapet is still a solid horizontal cut, and what
 *    reads as "a building you could stand on" is the thin, broken, see-through
 *    line above it. It is also the one roof piece that makes the pass's
 *    standing collision compromise VISUALLY HONEST — you can walk through a
 *    railing on screen, and a railing is a thing you can see through, so the
 *    silhouette no longer promises a wall that is not there.
 *  - A HATCH, so the roof has a way IN that is not the stair hut. Small roofs
 *    are too small for a hut and had nothing at all.
 *  - FAN COWLS on the condensers, which is what turns a low grey box into an
 *    air-handling unit at forty metres (the `louvre` surface does the other
 *    half of that job).
 *
 * COLLISION: the railing takes the parapet's documented exception, not a new
 * one — it stands on top of an upstand you can already walk through, entirely
 * within the parapet's own footprint, so it never widens the roof and never
 * blocks a line a player could previously walk. `ROOF_RAIL_H` is capped so the
 * whole assembly stays under `ROOF_PLANT_MAX_H`.
 */

/** Height of the railing above the coping it stands on. */
export const ROOF_RAIL_H = 0.95;
/** Posts per edge. 5 × 4 edges = 20 is the railing's whole post budget. */
export const ROOF_POSTS_PER_EDGE = 5;
/** Nominal post spacing; the real spacing is the edge length divided evenly. */
const RAIL_SPACING = 2.6;
/** How high the upstand under the railing is — the parapet on a building
 *  (`dressStructure`) or the kerb on a slab (`dressGround`). Must track those
 *  two emitters or the railing floats. */
function upstandH(surface: "building" | "slab", h: number): number {
  return surface === "building" ? Math.min(0.42, Math.max(0.26, h * 0.04)) : 0.16;
}

/** Does this map's fiction have bitumen-and-gravel roofs? Same PERIOD argument
 *  as `paintedStreets`, same list: a castle's leads and a schooner's deck are
 *  not built-up membrane, and a stone keep with a galvanised water tank on it
 *  is worse than the window texture this pass exists to remove. */
export function roofFelt(mapId: string | undefined): boolean {
  return !mapId || !HISTORIC_MAPS.has(mapId);
}

/** Maps whose gameplay happens ON the roofs — furniture would be a ghost
 *  obstacle in a fighting space. They still get the membrane, which is pure
 *  upside there: their FLOOR stops being a sheet of window glass. */
const ROOFTOP_MAPS = new Set(["rooftops", "rooftops_xl", "rooftop_spiderweb"]);
export function roofFurniture(mapId: string | undefined): boolean {
  return roofFelt(mapId) && (!mapId || !ROOFTOP_MAPS.has(mapId));
}

/** Does this SOLID structure have a roof worth dressing? Tall enough to be seen
 *  from above or across, and wide enough that a 0.4 m parapet is not most of
 *  it. */
export function roofDressable(w: number, h: number, d: number): boolean {
  return h >= 6 && Math.min(w, d) >= 5;
}

/** How high a flat slab's top must be before it is a ROOF and not a floor. */
export const ROOF_MIN_HEIGHT = 4;

/**
 * Is this flat slab a ROOF rather than a floor?
 *
 * This is the half of the pass that reaches most maps. Almost no building in
 * this game is a solid extrusion — village, neo_island, neon_district, the
 * rooftop maps and the compound all build a HOLLOW shell out of thin walls and
 * then lay a flat slab on top. The renderer classified that slab by shape
 * alone, so it came out `ground`/`paved`: the roofs of this game were wearing
 * the STREET, kerbs and (since the street pass) lane paint and manhole covers
 * included. Elevation is the one bit of information that distinguishes the two,
 * and no map declares it, so it is inferred here.
 */
export function elevatedSlab(w: number, h: number, d: number, topY: number): boolean {
  return h <= 1.2 && topY >= ROOF_MIN_HEIGHT && Math.min(w, d) >= 5 && Math.max(w, d) >= 8;
}

/** Deterministic in [0,1). The dressing must be identical on every client and
 *  across a round restart, so this hashes the WORLD POSITION rather than
 *  calling Math.random() — same discipline the shared sim keeps (invariant 7),
 *  applied here because a roof that rearranges itself on respawn is a bug. */
function hash01(x: number, z: number, salt: number): number {
  const s = Math.sin(x * 127.1 + z * 311.7 + salt * 74.7) * 43758.5453;
  return s - Math.floor(s);
}

/**
 * Emit the deck and (if `furniture`) the plant for one roof.
 *
 * `surface` says what is underneath, and it decides two things: how far in
 * everything has to sit to clear the edge piece already standing there (a
 * BUILDING has `dressStructure`'s parapet, a SLAB has `dressGround`'s kerb),
 * and whether a deck is emitted at all. A slab does not get one — it IS the
 * roof, and the renderer gives it the `roofing` material directly, which is
 * both cheaper and better than laying a second slab 7 cm above it.
 */
export function roofPieces(
  box: DressTarget, surface: "building" | "slab", furniture: boolean, out: RoofPiece[],
): void {
  const [w, h, d] = box.s;
  const cx = box.p[0], cz = box.p[2];
  const top = box.p[1] + h / 2;
  // MUST match the edge piece the other emitter put there — the parapet
  // thickness in `dressStructure`, the kerb width in `dressGround`. A mismatch
  // either leaves a bald ring or z-fights the upstand.
  const edge = surface === "building"
    ? Math.min(0.4, Math.max(0.16, Math.min(w, d) * 0.05))
    : Math.min(0.6, Math.max(0.34, Math.min(w, d) * 0.03));
  const inset = edge + 0.06;
  const rw = w - inset * 2, rd = d - inset * 2;
  if (rw < 1.5 || rd < 1.5) return;

  // THE DECK. Sits ON the roof, not sunk into it: the 7 cm is what a real
  // built-up membrane + ballast stands, and it keeps the slab off the roof's
  // depth values so nothing z-fights at a grazing angle.
  const deckTop = surface === "building" ? top + ROOF_DECK_H : top;
  if (surface === "building") {
    out.push({ kind: "deck", w: rw, h: ROOF_DECK_H, d: rd, x: cx, y: top + ROOF_DECK_H / 2, z: cz });
  }
  if (!furniture) return;

  const area = rw * rd;
  if (area < 34) return; // a 6x6 roof with a stair hut on it is all hut

  // The PERIMETER BAND. Everything below is placed with its footprint inside
  // this band, i.e. outside the central ROOF_CLEAR box — see the contract.
  const band = Math.min(2.8, Math.min(rw, rd) * (1 - ROOF_CLEAR) * 0.5);
  if (band < 0.9) return;
  /** Centre offset that puts a `size`-wide footprint flush against the `sign`
   *  edge of the `len`-long axis. Callers must keep `size <= band`; that is
   *  exactly the condition under which the piece's INNER face still clears the
   *  central ROOF_CLEAR box (inner face at `len/2 - size`, clear zone ends at
   *  `len * ROOF_CLEAR / 2`, and `band = min(rw,rd) * (1-ROOF_CLEAR)/2`). */
  const bandPos = (len: number, size: number, sign: number): number =>
    sign * (len / 2 - size / 2);

  // Which corner gets the stair hut, and which the tank — deterministic per
  // building, so two towers on the same street are not laid out identically.
  const r0 = hash01(cx, cz, 1), r1 = hash01(cx, cz, 2), r2 = hash01(cx, cz, 3);
  const hutSX = r0 < 0.5 ? -1 : 1, hutSZ = r1 < 0.5 ? -1 : 1;

  // STAIR HUT — the roof-access house. The tallest thing up here and the one
  // that reads at range, because it has a silhouette a person could walk into.
  {
    const s = Math.min(3.2, band, rw * 0.3, rd * 0.3);
    if (s >= 1.4) {
      const hh = Math.min(ROOF_PLANT_MAX_H - 0.2, Math.max(1.9, s * 0.75));
      out.push({
        kind: "hut", w: s, h: hh, d: s * 0.86,
        x: cx + bandPos(rw, s, hutSX), y: deckTop + hh / 2, z: cz + bandPos(rd, s * 0.86, hutSZ),
      });
    }
  }

  // WATER TANK on four legs, in the opposite corner. The legs are the point:
  // a cylinder sitting flat on a roof is a bollard, a cylinder standing on legs
  // with daylight under it is unmistakably a tank.
  if (area >= 68) {
    const rad = Math.min(1.15, Math.min(rw, rd) * 0.11, band * 0.5);
    const legH = 0.75, bodyH = Math.min(1.6, ROOF_PLANT_MAX_H - legH - 0.15);
    if (rad >= 0.45) {
      const tx = cx + bandPos(rw, rad * 2, -hutSX), tz = cz + bandPos(rd, rad * 2, -hutSZ);
      out.push({ kind: "tank", w: rad * 2, h: bodyH, d: rad * 2, x: tx, y: deckTop + legH + bodyH / 2, z: tz });
      const lo = rad * 0.62;
      for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
        out.push({ kind: "leg", w: 0.13, h: legH, d: 0.13, x: tx + sx * lo, y: deckTop + legH / 2, z: tz + sz * lo });
      }
    }
  }

  // CONDENSER UNITS — a row of low boxes along one long edge. Low, wide and
  // repeated: the horizontal counterweight to the hut's vertical.
  {
    const alongX = rw >= rd;
    const len = alongX ? rw : rd;
    const n = Math.max(1, Math.min(3, Math.floor(area / 55)));
    const uw = Math.min(2.1, len / (n + 1) * 0.8), ud = Math.min(1.25, band * 0.8);
    const uh = 0.95;
    if (uw >= 0.7 && ud >= 0.5) {
      // the edge the units line up on — the one the hut is NOT on, so the two
      // families of plant do not stack in the same corner
      const sign = alongX ? -hutSZ : -hutSX;
      const across = alongX ? bandPos(rd, ud, sign) : bandPos(rw, ud, sign);
      for (let i = 0; i < n; i++) {
        const t = (i + 1) / (n + 1) - 0.5;
        const ux = cx + (alongX ? t * len * 0.82 : across);
        const uz = cz + (alongX ? across : t * len * 0.82);
        out.push({
          kind: "plant",
          w: alongX ? uw : ud, h: uh, d: alongX ? ud : uw,
          x: ux, y: deckTop + uh / 2, z: uz,
        });
        // ROUND TWO — the FAN COWL. A low grey box is a crate; a low grey box
        // with a recessed circular fan in its lid is unmistakably an air
        // handler, and the ring is the one detail that still resolves at 40 m
        // because it is the only CURVE in the roof's silhouette.
        // sized off the unit's SHORT axis (`ud` is the across-the-band one), so
        // the cowl can never overhang the housing it sits on
        const fd = Math.min(uw, ud) * 0.82;
        if (fd >= 0.42) {
          out.push({ kind: "fan", w: fd, h: 0.16, d: fd, x: ux, y: deckTop + uh + 0.05, z: uz });
        }
      }
    }
  }

  // VENT STACKS — thin pipes of three different heights. Cheap, and the
  // strongest "this is machinery" cue per triangle on the whole roof.
  {
    const alongX = rw >= rd;
    const len = alongX ? rw : rd;
    const sign = alongX ? hutSZ : hutSX;      // opposite edge from the condensers
    const across = alongX ? bandPos(rd, 0.4, sign) : bandPos(rw, 0.4, sign);
    const n = area >= 90 ? 3 : 2;
    for (let i = 0; i < n; i++) {
      const sh = 0.9 + hash01(cx + i * 3.3, cz, 4 + i) * 0.9;
      const t = (i + 1) / (n + 1) - 0.5;
      out.push({
        kind: "stack", w: 0.34, h: sh, d: 0.34,
        x: cx + (alongX ? t * len * 0.6 : across),
        y: deckTop + sh / 2,
        z: cz + (alongX ? across : t * len * 0.6),
      });
    }
  }

  // ROOF HATCH — the way in on a roof whose access house is in the far corner.
  // Flush with the deck edge in the corner diagonally opposite the hut, so the
  // two access points read as belonging to different stairs. The renderer turns
  // this one piece into a lipped kerb plus a lid tilted open on it.
  {
    const hw = Math.min(1.25, band, rw * 0.2), hd = Math.min(1.0, band, rd * 0.2);
    if (hw >= 0.6 && hd >= 0.55) {
      out.push({
        kind: "hatch", w: hw, h: 0.2, d: hd,
        x: cx + bandPos(rw, hw, -hutSX), y: deckTop + 0.1, z: cz + bandPos(rd, hd, hutSZ),
      });
    }
  }

  // ============ THE PARAPET RAILING ============
  // Posts and two horizontal rails standing on the coping, all the way round.
  // See the ROUND TWO note above for why this is the piece that changes the
  // skyline and why it is allowed to be a ghost.
  //
  // WHERE IT STANDS. On a BUILDING it sits centred on `dressStructure`'s
  // parapet — outside the deck rectangle, so it clears the hut and the tank
  // standing flush against that rectangle's edge, and inboard of the collider
  // footprint by half the parapet thickness. On a SLAB there is no guaranteed
  // upstand (`dressGround` kerbs only the OPEN sides), so it stands directly on
  // the slab just inside where a kerb would be — which also keeps the slab's
  // "every piece stands ON the slab" contract intact.
  {
    const onBuilding = surface === "building";
    const railY0 = onBuilding ? top + upstandH(surface, h) : top;
    const offX = onBuilding ? w / 2 - edge / 2 : rw / 2;
    const offZ = onBuilding ? d / 2 - edge / 2 : rd / 2;
    const POST = 0.09, TOP_R = 0.075, MID_R = 0.055;
    const spanX = offX * 2 - 0.04, spanZ = offZ * 2 - 0.04;
    const topY = railY0 + ROOF_RAIL_H - TOP_R / 2;
    const midY = railY0 + ROOF_RAIL_H * 0.5;
    for (const s of [-1, 1]) {
      // rails along X, at both Z edges (they carry the corners)
      out.push({ kind: "rail", w: spanX, h: TOP_R, d: TOP_R, x: cx, y: topY, z: cz + s * offZ });
      out.push({ kind: "rail", w: spanX, h: MID_R, d: MID_R, x: cx, y: midY, z: cz + s * offZ });
      // rails along Z, shortened so they butt into the X rails instead of
      // crossing them at the corner
      out.push({ kind: "rail", w: TOP_R, h: TOP_R, d: spanZ - TOP_R * 2, x: cx + s * offX, y: topY, z: cz });
      out.push({ kind: "rail", w: MID_R, h: MID_R, d: spanZ - MID_R * 2, x: cx + s * offX, y: midY, z: cz });
    }
    // POSTS. The X edges carry the corner posts (endpoints included); the Z
    // edges only fill their interior, so no corner gets two posts in it.
    const nx = Math.max(2, Math.min(ROOF_POSTS_PER_EDGE, Math.round(spanX / RAIL_SPACING)));
    const nz = Math.max(0, Math.min(ROOF_POSTS_PER_EDGE, Math.round(spanZ / RAIL_SPACING) - 1));
    for (const s of [-1, 1]) {
      for (let i = 0; i <= nx; i++) {
        const px = cx + (i / nx - 0.5) * spanX;
        out.push({ kind: "post", w: POST, h: ROOF_RAIL_H, d: POST, x: px, y: railY0 + ROOF_RAIL_H / 2, z: cz + s * offZ });
      }
      for (let i = 1; i <= nz; i++) {
        const pz = cz + (i / (nz + 1) - 0.5) * spanZ;
        out.push({ kind: "post", w: POST, h: ROOF_RAIL_H, d: POST, x: cx + s * offX, y: railY0 + ROOF_RAIL_H / 2, z: pz });
      }
    }
  }
}

/* ===========================================================================
 * STREET LEVEL & SIGNAGE (2026-08-05, mission `graphics`).
 *
 * Twenty graphics passes had gone over these buildings: they have a plinth, a
 * belt course, window-bay mullions, a cornice, a parapet, a roof full of plant
 * and a railing round it. What they still did not have is the one thing that
 * makes a street a street rather than an architectural model — they did not
 * SAY anything, and nothing about them was lit from the inside except a texture
 * of office windows. The game is called NEON PURGE.
 *
 * Three pieces, all hung off the entrance the `dressStructure` pass already
 * chose (`entranceFace`), so they land on the face a player actually walks up
 * to instead of on a random wall:
 *
 *  1. An AWNING over the door — a sloped, STRIPED fabric canopy with a hanging
 *     valance and two stay rods. This is the piece that changes the silhouette
 *     at eye level: it is the first thing in this whole map-dressing family
 *     that is not grey, not stone, and not parallel to a wall.
 *  2. A FASCIA sign above it, flush to the wall, carrying a lit face.
 *  3. A BLADE sign high up, hanging PERPENDICULAR out over the pavement with a
 *     lit face on each side. This is the piece that changes the silhouette at
 *     range: every other thing this module has ever emitted hugs the wall, so
 *     a facade's outline has always been a straight vertical line from plinth
 *     to parapet. A blade is the only thing that breaks it.
 *
 * COLLISION CONTRACT — stricter than the rest of the file, because these are
 * the first pieces that reach a whole metre outside the collider.
 *  - Anything projecting further than the file's 0.26 m ledge allowance must
 *    have its UNDERSIDE at least `SIGN_MIN_CLEAR` above the structure's base.
 *    That is above head height and above a standing jump, so the ghost is
 *    never something a player tries to stand on or walks into. If the door is
 *    too low for the awning to clear that, the awning is simply not emitted.
 *  - Nothing may reach above the roof line (the parapet and the roof railing
 *    own that band, and a blade poking through a deck would be a bug).
 *  - Nothing lateral may leave the face it is mounted on.
 * `agent/bench-architecture.mjs` section F asserts all three over every map.
 *
 * COST: same as everything else here — merged once at map load into two extra
 * meshes for the WHOLE MAP (one lit `signage` atlas, one `cloth` awning); the
 * steel goes into the street-ironwork mesh that already existed. Never a frame
 * of work.
 * ========================================================================= */

/** One emitted signage piece. `w/h/d` are UNROTATED full extents about `x,y,z`;
 *  a piece with a `tilt` is rotated about the wall-tangent axis afterwards (its
 *  OUTWARD end drops), and `x,y,z` is already the post-rotation centre. */
export interface SignPiece {
  kind: "blade" | "arm" | "fascia" | "canopy" | "valance" | "rod" | "face";
  w: number; h: number; d: number;
  x: number; y: number; z: number;
  /** outward normal of the face this piece hangs off (unit, axis-aligned) */
  nx: number; nz: number;
  /** `face` only: which of the 4 atlas cells this quad shows */
  cell: number;
  /** `canopy`/`valance`/`rod` only: stripe index, for the colour alternation */
  stripe: number;
  /** radians; 0 for everything except the canopy and its rods */
  tilt: number;
}

/** Bound the caller uses for all-or-nothing emission, like DRESS_MAX_BOXES. */
export const SIGN_MAX_PIECES = 24;
/** How far the furthest piece reaches outside the collider wall. */
export const SIGN_PROJECT = 1.1;
/** Minimum underside height, above the structure's base, for anything that
 *  projects further than the file's ledge allowance. Head height plus a jump. */
export const SIGN_MIN_CLEAR = 2.2;
/** Stripes across one awning. Six is what makes it read as fabric rather than
 *  as a painted board — and they are separate boxes only because a flat vertex
 *  colour is how the merged cloth mesh gets two tones out of one material. */
export const AWNING_STRIPES = 6;

/** Does this structure get signage?
 *
 *  Deliberately LOOSER than the entrance surround's gate, and this is the one
 *  judgement call in the section. The surround needs an occupiable thickness
 *  (`min(w,d) >= 2.2`) because a door in a 1 m wall would be 0.55 m wide and
 *  read as a rivet. A SIGN has no such problem: it hangs on the face, and the
 *  face of a thin-slab tower is as big as any other. Excluding them would have
 *  left neon_district — a whole skyline built from 1–2 m slabs, and the map
 *  most obviously named after this feature — with no signage at all, which is
 *  the same trap the ground pass caught the building pass in on 2026-08-01.
 *  `facadeSigns` still bails if the face it picks is the narrow END of a slab.
 */
export function signable(w: number, h: number, d: number): boolean {
  return h >= 6 && Math.max(w, d) >= 4.5 && Math.min(w, d) >= 0.3;
}

/** The narrowest face worth hanging a shopfront on. Below this the awning is
 *  wider than the wall behind it. */
export const SIGN_MIN_FACE = 3.2;

/**
 * Emit the awning, the fascia sign and (on a tall enough facade) the blade sign
 * for one structure.
 *
 * `lit` is false on historic maps: a medieval keep gets its awning and its
 * hanging board through the same code, it just gets no glowing tubes. In
 * practice the caller opts those maps out entirely today (`paintedStreets`),
 * but the parameter is here so a later pass can turn painted shop boards on for
 * the wild-west street without touching this geometry.
 */
export function facadeSigns(
  box: DressTarget, lit: boolean, out: SignPiece[], outward = 0,
): void {
  const [w, h, d] = box.s;
  const x0 = box.p[0], y0 = box.p[1], z0 = box.p[2];
  if (!signable(w, h, d)) return;
  const face = entranceFace(x0, z0, w, d);
  const faceX = face.faceX;
  // `outward` (from `shellOutward`) overrules the map-centre rule when the
  // caller knows this wall is one side of a hollow shell — see that function.
  const sign = outward !== 0 ? outward : face.sign;
  const base = y0 - h / 2;
  /** half-depth of the structure along the face's OUTWARD axis */
  const halfN = (faceX ? w : d) / 2;
  /** length of the face along its tangent axis */
  const faceLen = faceX ? d : w;
  const nx = faceX ? sign : 0, nz = faceX ? 0 : sign;
  /** place a piece from (outward offset from the wall, tangent offset, height) */
  const put = (
    kind: SignPiece["kind"], along: number, tan: number, y: number,
    extN: number, extT: number, extY: number,
    cell = 0, stripe = 0, tilt = 0,
  ): void => {
    const px = faceX ? x0 + sign * (halfN + along) : x0 + tan;
    const pz = faceX ? z0 + tan : z0 + sign * (halfN + along);
    out.push({
      kind,
      w: faceX ? extN : extT, h: extY, d: faceX ? extT : extN,
      x: px, y, z: pz, nx, nz, cell, stripe, tilt,
    });
  };

  if (faceLen < SIGN_MIN_FACE) return;   // the narrow END of a slab tower

  // The entrance this all hangs off — when there IS one, these four numbers
  // MUST match `dressStructure`'s entrance surround or the awning floats off
  // the lintel. A thin slab gets no surround (see `signable`), so its shopfront
  // is sized off the FACE instead of off the wall's thickness.
  const hasDoor = Math.min(w, d) >= 2.2;
  const doorW = hasDoor
    ? Math.min(3.2, Math.min(w, d) * 0.55)
    : Math.min(3.2, Math.max(1.8, faceLen * 0.16));
  const doorH = Math.min(3.4, h * 0.42);
  const jamb = Math.max(0.22, doorW * 0.13);
  const lintelTop = base + doorH + jamb / 2;

  // ---- 1. THE AWNING ----------------------------------------------------
  const TILT = 0.30;                 // ~17deg — a real shop awning's fall
  const CANOPY_T = 0.07;
  const VAL_H = 0.20;
  const hinge = lintelTop + 0.07;    // the wall end of the canopy
  // The contract, enforced by SHRINKING rather than by refusing. A canopy you
  // walk through is worse than no canopy, so the leading edge must clear head
  // height — but the first cut of this simply skipped any building whose door
  // was too low, and that is nearly every house in the village and every wall
  // in the compound (they are all exactly 6 m). Reeling the projection in until
  // the valance clears keeps a real, if shallower, awning on those; under 0.6 m
  // it stops being an awning and is dropped.
  const drop = hinge - VAL_H - CANOPY_T - (base + SIGN_MIN_CLEAR);
  const proj = Math.min(SIGN_PROJECT, drop / Math.sin(TILT));
  const outerY = hinge - proj * Math.sin(TILT);
  if (proj >= 0.6) {
    const awW = Math.max(doorW * 1.2, Math.min(faceLen - 0.6, doorW * 1.75));
    const sw = awW / AWNING_STRIPES;
    // centre of the sloped slab, in the (outward, height) plane
    const cAlong = (proj / 2) * Math.cos(TILT);
    const cY = hinge - (proj / 2) * Math.sin(TILT);
    for (let i = 0; i < AWNING_STRIPES; i++) {
      const t = (i + 0.5 - AWNING_STRIPES / 2) * sw;
      put("canopy", cAlong, t, cY, proj, sw, CANOPY_T, 0, i, TILT);
      // the VALANCE — the short vertical lip hanging off the leading edge. It
      // is what casts the shadow line that reads as "fabric with weight", and
      // it is the only part of the awning visible from directly underneath.
      put("valance", proj * Math.cos(TILT), t, outerY - VAL_H / 2, 0.05, sw, VAL_H, 0, i, 0);
    }
    // STAY RODS under the outer corners: an awning with no visible support
    // reads as a floating shelf.
    for (const s of [-1, 1]) {
      put("rod", cAlong, s * (awW / 2 - 0.09), cY - 0.075, proj * 0.98, 0.055, 0.055, 0, 0, TILT);
    }
  }

  // ---- 2. THE FASCIA SIGN -----------------------------------------------
  const fw = Math.max(doorW, Math.min(faceLen - 0.5, doorW * 1.9));
  // Height is keyed to WIDTH, not to the building. The first capture of this
  // pass had a 5.5 m fascia 0.44 m tall — a 12:1 letterbox — and the atlas cell
  // is square, so the sign's glyphs arrived squashed into an illegible smear.
  // A real shop fascia is about a quarter as tall as it is wide.
  const fh = Math.min(1.3, Math.max(0.5, fw * 0.26));
  const fd = 0.22;                   // inside the 0.26 m ledge allowance
  const fy = hinge + 0.34 + fh / 2;
  if (fy + fh / 2 < y0 + h / 2 - 0.6) {
    put("fascia", fd / 2, 0, fy, fd, fw, fh);
    if (lit) put("face", fd + 0.012, 0, fy, 0, fw - 0.13, fh - 0.13, signCell(x0, z0, 1));
  }

  // ---- 3. THE BLADE SIGN ------------------------------------------------
  // Needs a facade tall enough to hang one where nobody can reach it: the top
  // must clear the parapet band and the bottom must clear SIGN_MIN_CLEAR by a
  // wide margin, because this is the piece that projects a full metre.
  const bTop = y0 + h / 2 - Math.max(0.9, h * 0.10);
  const bH = Math.min(4.4, h * 0.30);
  const bThick = 0.22;
  if (bTop - bH >= base + 3.4 && faceLen >= 4.5) {
    // hung near one end of the face, on the side the hash picks — a blade over
    // the middle of the door fights the fascia for the same silhouette
    const side = signCell(x0, z0, 7) < 2 ? -1 : 1;
    const tOff = side * (faceLen / 2 - Math.max(1.1, faceLen * 0.18));
    const bY = bTop - bH / 2;
    put("blade", proj / 2, tOff, bY, proj, bThick, bH);
    // the BRACKET: a spine bolted flat to the wall plus a rail over the top.
    // Without them the blade is a slab intersecting a wall; with them it hangs.
    put("arm", 0.08, tOff, bY, 0.16, 0.34, bH * 1.04);
    put("arm", proj * 0.5, tOff, bTop + 0.07, proj, 0.07, 0.07);
    if (lit) {
      for (const s of [-1, 1]) {
        put("face", proj / 2, tOff + s * (bThick / 2 + 0.012), bY,
          proj - 0.14, 0, bH - 0.18, signCell(x0, z0, s > 0 ? 3 : 5));
        // the outward-facing quad is oriented by the caller off `stripe`: it is
        // the ONE piece whose lit side does not point along (nx,nz)
        out[out.length - 1].stripe = s;
      }
    }
  }
}

/**
 * Which way is OUT, for a wall that is one side of a hollow shell.
 *
 * `entranceFace` picks the side facing the middle of the MAP, which is right
 * for a freestanding building and a coin flip for a shell: almost every house
 * in this game is four thin walls and a roof slab, and for two of those four
 * walls "toward the map centre" points into the living room. The first capture
 * of this pass is a lit shop sign glowing inside a shed.
 *
 * The shell's own centre is the missing information and it is recoverable
 * without any new map data: take the other THIN, similarly-tall boxes nearby,
 * average them, and point away from that. For the compound's `[18,6,0.4]` wall
 * at z=+10 the other three walls average to the middle of the hut, so out is
 * +Z — the yard — and not the room behind it.
 *
 * Returns 0 for "no opinion" (nothing shell-like around it, or the wall runs
 * through the middle of the group), and the caller falls back to the map-centre
 * rule. Deliberately restricted to THIN boxes: a solid building has a real
 * front and `entranceFace` already knows where it is.
 */
export const SHELL_RADIUS = 14;
/** `caps`: the flat slabs (roofs / upper floors) of the map, for the roof test
 *  below. Defaults to `others`; callers that pass only walls as `others` must
 *  pass the slabs here or the roof test cannot fire. */
export function shellOutward(box: DressTarget, others: readonly DressTarget[], caps: readonly DressTarget[] = others): number {
  const [w, h, d] = box.s;
  if (Math.min(w, d) >= 2.2) return 0;                  // solid: it has a real front
  const { faceX } = entranceFace(box.p[0], box.p[2], w, d);
  const n = faceX ? 0 : 2;                              // index of the outward axis
  const top = box.p[1] + h / 2, bot = box.p[1] - h / 2;
  // ROOF TEST FIRST (2026-11-04). The inside of a hollow house is the side its
  // roof slab covers. The neighbour-mean below is easily outvoted by the walls
  // of the NEXT house or a perimeter wall inside SHELL_RADIUS, which put 110
  // window pieces inside village rooms and 370 inside Wild West ones
  // (agent/bench-architecture.mjs now asserts none are). Probe 0.8 m off each
  // face at mid-height: if exactly one side sits under a slab that caps this
  // wall, the other side is outward.
  const half = (n === 0 ? w : d) / 2;
  const covered = (side: number): boolean => {
    const px = box.p[0] + (n === 0 ? side * (half + 0.8) : 0);
    const pz = box.p[2] + (n === 2 ? side * (half + 0.8) : 0);
    for (const o of caps) {
      if (o === box || o.s[1] > 1.2 || Math.max(o.s[0], o.s[2]) < 3) continue;
      const ob = o.p[1] - o.s[1] / 2;
      if (ob < top - 0.8 || ob > top + 1.5) continue;
      if (Math.abs(px - o.p[0]) < o.s[0] / 2 && Math.abs(pz - o.p[2]) < o.s[2] / 2) return true;
    }
    return false;
  };
  const cPos = covered(1), cNeg = covered(-1);
  if (cPos !== cNeg) return cPos ? -1 : 1;
  let sum = 0, count = 0;
  for (const o of others) {
    if (o === box) continue;
    if (Math.min(o.s[0], o.s[2]) >= 2.2) continue;      // shells are made of thin walls
    // must overlap in height (a wall two storeys up is a different building)
    if (o.p[1] + o.s[1] / 2 < bot + 0.5 || o.p[1] - o.s[1] / 2 > top - 0.5) continue;
    const dx = o.p[0] - box.p[0], dz = o.p[2] - box.p[2];
    if (dx * dx + dz * dz > SHELL_RADIUS * SHELL_RADIUS) continue;
    sum += n === 0 ? dx : dz;
    count++;
  }
  if (count < 2) return 0;                              // not enough to call it a shell
  const mean = sum / count;
  return Math.abs(mean) < 0.5 ? 0 : (mean < 0 ? 1 : -1);
}

/** Which of the four atlas cells this sign shows. Position-hashed for the same
 *  reason `roofPieces` hashes: identical on every client and across a round
 *  restart, and a street whose signs re-roll their colours on respawn is a bug.
 *  `salt` separates the several signs on one building. */
export function signCell(x: number, z: number, salt: number): number {
  return Math.min(3, (hash01(x, z, salt * 13.3 + 5) * 4) | 0);
}

/* ===========================================================================
 * THE SHOPFRONT (2026-08-05, mission `graphics`) — the ground storey.
 *
 * The signage pass hung an awning, a fascia and a blade over every entrance in
 * the game. What it pointed at was a BLANK WALL: the whole ground storey was
 * one flat rectangle of facade tile, and an awning over nothing is more
 * obviously empty than no awning was. This is the previous pass's own ranked
 * idea #1.
 *
 * A real shopfront is five things, and all five are geometry, not texture:
 *
 *  1. A STALL RISER — the solid panel under the glazing. It is why a shop
 *     window starts at waist height instead of at the pavement, and it is the
 *     single strongest "this is a shop" cue at 20 m.
 *  2. A CILL over it, standing proud enough to throw a horizontal shadow line
 *     across the front. Every other horizontal in this file (plinth, belt
 *     course, cornice) exists for exactly this reason and it works.
 *  3. GLAZING — a dark, glossy, INTERNALLY LIT band, divided by real MULLIONS
 *     into panes. This is the first surface on any map that shows something
 *     BEHIND it, and the second in the game lit from the inside.
 *  4. PIERS at each end and a TRANSOM bar across the top, which is the frame
 *     that makes the glazing read as an opening in a wall rather than a dark
 *     rectangle painted on one.
 *  5. A DOOR in the middle — leaf, kick panel, glazed upper light and a pull
 *     handle — landing under the same door the entrance surround already
 *     draws, so the awning, the surround and the shopfront agree.
 *
 * COLLISION CONTRACT — the STRICTEST in the file, and deliberately stricter
 * than the signage pass's. Every one of these pieces is at ground level, i.e.
 * exactly where a player walks, so NONE of them may buy projection with height
 * the way an awning or a blade does. Everything here stays inside the file's
 * 0.26 m ledge allowance (`SHOP_PROJECT`), nothing reaches above the awning
 * hinge, and nothing leaves the face it is mounted on.
 *
 * Why the glazing is 2 cm PROUD and not recessed: the wall is one solid
 * collider box and the client cannot cut a hole in it, so a pane set behind the
 * wall face would be hidden by that face. The recession is read from the FRAME
 * instead — mullions and piers stand 0.14 m proud of glass that stands 0.02 m
 * proud, and that 0.12 m step is what the eye reads as depth. The entrance
 * surround makes the same trade (its jambs are sunk inside the footprint and
 * read purely off the material change).
 *
 * COST: one extra merged mesh for the WHOLE MAP (the glazing, which needs its
 * own emissive atlas). The masonry — riser, cill, piers — goes into the trim
 * detail mesh that already exists, and all the metalwork into the street
 * ironwork mesh, so both are free. Built once at map load; never a frame of
 * work.
 * ========================================================================= */

/** One emitted shopfront piece. `w/h/d` are full extents about `x,y,z`.
 *  `u0`/`u1` are set on `glass` only: the pane's span across the shopfront as a
 *  fraction, so the caller can map ONE continuous interior behind every pane of
 *  a front instead of repeating the same shelf in each. */
export interface ShopPiece {
  kind: "riser" | "cill" | "pier" | "transom" | "mullion" | "glass"
    | "jamb" | "door" | "kick" | "handle";
  w: number; h: number; d: number;
  x: number; y: number; z: number;
  /** outward normal of the face this piece sits on (unit, axis-aligned) */
  nx: number; nz: number;
  /** `glass` only: which of the 4 interior atlas cells this front shows */
  cell: number;
  /** `glass` only: fractional span across the pane's OWN room, left to right */
  u0: number; u1: number;
  /** `glass` only: which shop bay of this front the pane belongs to. Panes in
   *  one bay share a `cell` and tile its u range exactly once — see the bay
   *  rule in `shopfront`. */
  room: number;
}

/** Bound the caller uses for all-or-nothing emission, like DRESS_MAX_BOXES. */
export const SHOP_MAX_PIECES = 34;
/** Hard projection cap. This is the file's ordinary ledge allowance and there
 *  is no exception to it here: everything a shopfront emits is at head height
 *  or below, where a ghost is something you walk into. */
export const SHOP_PROJECT = 0.26;
/** Height of the solid panel under the glazing. */
export const SHOP_RISER_H = 0.8;
/** Shortest glazing band worth emitting — under this the shopfront is a slot. */
export const SHOP_MIN_GLASS = 0.75;
/** Widest a single shopfront may run. Past this it stops being a shop and
 *  becomes a curtain wall, and its panes get too big to read as panes. */
export const SHOP_MAX_W = 11;
/** Target pane width. Real shopfront mullion grids land near this; a single 3 m
 *  sheet reads as a hole in the wall and four narrow ones as a cage. */
export const SHOP_PANE_W = 1.45;
/** Target width of ONE shop interior. A long front carries several — a street
 *  is a row of shops, and one 11 m room stretched over a whole facade is both
 *  wrong and (at 23 texels per metre) an illegible smear. */
export const SHOP_ROOM_W = 2.8;

/**
 * Emit the ground-storey shopfront for one structure.
 *
 * Deliberately shares `facadeSigns`' gates and its face choice: the awning must
 * land over the glazing, not over the wall next to it. `outward` comes from
 * `shellOutward` and must be the SAME value the caller passed to `facadeSigns`.
 */
export function shopfront(box: DressTarget, out: ShopPiece[], outward = 0): void {
  const [w, h, d] = box.s;
  const x0 = box.p[0], y0 = box.p[1], z0 = box.p[2];
  if (!signable(w, h, d)) return;
  const face = entranceFace(x0, z0, w, d);
  const faceX = face.faceX;
  const sign = outward !== 0 ? outward : face.sign;
  const base = y0 - h / 2;
  const halfN = (faceX ? w : d) / 2;
  const faceLen = faceX ? d : w;
  if (faceLen < SIGN_MIN_FACE) return;
  const nx = faceX ? sign : 0, nz = faceX ? 0 : sign;
  const put = (
    kind: ShopPiece["kind"], along: number, tan: number, y: number,
    extN: number, extT: number, extY: number,
    cell = 0, u0 = 0, u1 = 0, room = 0,
  ): void => {
    out.push({
      kind,
      w: faceX ? extN : extT, h: extY, d: faceX ? extT : extN,
      x: faceX ? x0 + sign * (halfN + along) : x0 + tan,
      y,
      z: faceX ? z0 + tan : z0 + sign * (halfN + along),
      nx, nz, cell, u0, u1, room,
    });
  };

  // These four MUST match `facadeSigns` (which matches `dressStructure`'s
  // entrance surround) or the awning ends up over the wrong metre of wall.
  const hasDoor = Math.min(w, d) >= 2.2;
  const doorW = hasDoor
    ? Math.min(3.2, Math.min(w, d) * 0.55)
    : Math.min(3.2, Math.max(1.8, faceLen * 0.16));
  const doorH = Math.min(3.4, h * 0.42);
  // The awning's own width, which the shopfront must be at least as wide as —
  // a canopy wider than the glazing under it looks like a canopy over a wall.
  const awW = Math.max(doorW * 1.2, Math.min(faceLen - 0.6, doorW * 1.75));
  // ...but a SHOP takes a real bite out of its facade, so the front runs wider
  // than the canopy and the canopy shelters its middle. Clamped at both ends:
  // never off the face (the lateral violation the bench checks for), and never
  // past `SHOP_MAX_W`, because a 24 m sheet of glass on a 40 m wall is a
  // curtain wall, not a shop.
  const shopW = Math.min(faceLen - 0.8, SHOP_MAX_W, Math.max(awW, faceLen * 0.6));
  if (shopW < 1.8 || shopW < awW - 1e-9) return;

  const riserH = Math.min(SHOP_RISER_H, doorH * 0.3);
  const yRiser = base + riserH;               // top of the riser = cill line
  const yHead = base + doorH;                 // underside of the transom bar
  const glassH = yHead - yRiser - 0.10;       // 0.10 = the cill's own thickness
  if (glassH < SHOP_MIN_GLASS) return;        // no room for a window: emit nothing
  const yGlass = yRiser + 0.10 + glassH / 2;

  const half = shopW / 2;
  const PIER_W = 0.36, PIER_N = 0.19;         // pier width / projection
  const GLASS_N = 0.02, MULL_N = 0.14;
  const inner = half - PIER_W;                // inside face of each pier

  // ---- PIERS + TRANSOM: the frame round the opening -----------------------
  // The top of both stops at `yHead + 0.16`, which is under the awning hinge
  // (`lintelTop + 0.07`, and `jamb` is never below 0.22) on every structure —
  // the frame must not fight the canopy for the same band.
  const frameTop = yHead + 0.16;
  for (const s of [-1, 1]) {
    put("pier", PIER_N / 2, s * (half - PIER_W / 2), (base + frameTop) / 2,
      PIER_N, PIER_W, frameTop - base);
  }
  put("transom", 0.16 / 2, 0, yHead + 0.08, 0.16, shopW, 0.16);

  // ---- STALL RISER + CILL -------------------------------------------------
  const bandW = inner * 2;
  put("riser", 0.11 / 2, 0, base + riserH / 2, 0.11, bandW, riserH);
  // the cill stands proudest of anything here: it is the horizontal shadow
  // line, and a shadow line you cannot see is a piece of geometry wasted
  put("cill", SHOP_PROJECT / 2, 0, yRiser + 0.05, SHOP_PROJECT, bandW + 0.12, 0.10);

  // ---- THE DOOR -----------------------------------------------------------
  // Centred, under the entrance surround. The leaf is nearly flush and the
  // jambs stand proud of it — that step is the whole recession cue.
  const leafW = doorW - 0.22;
  const kickH = Math.min(0.62, doorH * 0.22);
  // DEPTH ORDER, and it is not cosmetic: the leaf sits BEHIND its own glazing,
  // the kick rail in front of it and the jambs in front of that. The first cut
  // put the pane at 0.02 and the 0.05 m leaf at 0.03, i.e. the glass INSIDE the
  // door — and the capture showed a shop with a sheet-metal panel where its
  // window should be.
  put("door", 0.02, 0, base + (yHead - base) / 2, 0.04, leafW, yHead - base);
  put("kick", 0.06, 0, base + kickH / 2, 0.07, leafW - 0.04, kickH);
  for (const s of [-1, 1]) {
    put("jamb", 0.13 / 2, s * (doorW / 2 - 0.055), base + (yHead - base) / 2,
      0.13, 0.11, yHead - base);
  }
  put("handle", 0.13, leafW / 2 - 0.16, base + (yHead - base) * 0.52, 0.06, 0.05,
    Math.min(1.0, (yHead - base) * 0.34));
  // the door's own upper light — the middle of a room, so a player walking up
  // to the door looks THROUGH it
  const dTop = yHead - 0.1;
  const dBot = base + kickH + 0.08;
  if (dTop - dBot > SHOP_MIN_GLASS) {
    put("glass", 0.05, 0, (dTop + dBot) / 2, 0, leafW - 0.12, dTop - dBot,
      signCell(x0, z0, 21), 0.28, 0.72, 0);
  }

  // ---- THE GLAZING, one shoulder each side of the door --------------------
  for (const s of [-1, 1]) {
    const a = s * (doorW / 2 + 0.11);         // inside edge (at the door jamb)
    const b = s * inner;                      // outside edge (at the pier)
    const span = Math.abs(b - a);
    if (span < 0.5) continue;
    const n = Math.max(1, Math.min(4, Math.ceil(span / SHOP_PANE_W)));
    const paneW = span / n;
    // ONE ROOM PER BAY, not one room per FRONT. The first cut ran a single
    // interior across the whole shopfront: on an 11 m front that is 23 px of
    // texture per metre, and the capture showed exactly what that predicts —
    // a flat smear with no legible shelf in it. A room every ~2.8 m is 3x the
    // resolution AND is what a real street is: three shops, not one window.
    // The bay boundary is placed on a MULLION (a whole number of panes per
    // room, never a fraction), so no pane straddles two rooms — and the panes
    // are shared out as EVENLY as they divide, so every room is tiled by its
    // panes exactly once. A room showing only two thirds of itself is the
    // failure mode here, and `bench-architecture` section G asserts against it.
    const rooms = Math.max(1, Math.min(n, Math.round(span / SHOP_ROOM_W)));
    for (let i = 0; i < n; i++) {
      const t0 = a + s * (i * paneW), t1 = a + s * ((i + 1) * paneW);
      const mid = (t0 + t1) / 2;
      const room = ((i * rooms) / n) | 0;
      // which pane of its own room this is, and how many that room has
      let first = 0, count = 0;
      for (let j = 0; j < n; j++) {
        if ((((j * rooms) / n) | 0) !== room) continue;
        if (count === 0) first = j;
        count++;
      }
      // u runs left→right across THIS ROOM, so the interior is continuous
      // behind the mullions that divide it
      const k = s < 0 ? count - 1 - (i - first) : i - first;
      const uA = k / count, uB = (k + 1) / count;
      const inset = 0.035 / (paneW * count);
      // the door light is bay 0; the two shoulders count outward from it
      const bay = s > 0 ? 1 + room : -1 - room;
      // the BAY's own centre — every pane of one bay must hash to the same
      // shop, so this cannot be keyed on the pane
      const bayMid = a + s * (first + count / 2) * paneW;
      // the bay's shop, hashed off the bay's OWN POSITION rather than off a
      // salt: a salt-only hash gave three neighbouring bays the same cell on
      // half the walls of the compound (the capture caught it), because the
      // hash's x/z terms dominate and the salt only nudges it.
      put("glass", GLASS_N, mid, yGlass, 0, paneW - 0.07, glassH,
        signCell(x0 + bayMid * 0.7 + bay * 5.3, z0 - bayMid * 0.9 + bay * 3.1, 21),
        uA + inset, uB - inset, bay);
      // the MULLION on the pier side of each pane (the door jamb already
      // closes the other end of the run)
      if (i === n - 1) continue;
      put("mullion", MULL_N / 2, t1, yGlass, MULL_N, 0.09, glassH + 0.04);
    }
  }
}

/* ===========================================================================
 * THE FLOOR CONTACT (2026-08-05, mission `graphics`).
 *
 * Look at any capture from before this pass and the single loudest "toy" tell
 * is not a surface and not a silhouette — it is that NOTHING IN THIS GAME
 * TOUCHES THE GROUND. A twenty-metre tower meets a plaza with a knife edge and
 * no darkening whatsoever, so it reads as a sticker laid over a flat plane
 * rather than a mass standing on one. Two reasons it was never fixed:
 *
 *  1. The renderer's cost ladder DISABLES the shadow map at L1 (`renderer.ts`),
 *     which every mid machine reaches within seconds. Real shadows are exactly
 *     the thing the game gives up first, so the grounding cue cannot depend on
 *     them.
 *  2. The floor is one huge slab wearing one tiled texture over hundreds of
 *     square metres. Tiling at that scale reads as a GRID, and a grid is the
 *     other half of the flatness.
 *
 * So: bake the contact. Everything here is a horizontal quad lying a few
 * centimetres above the floor, wearing the `grime` atlas (see mapTextures), and
 * merged into ONE mesh with MULTIPLY blending — so the darkening scales with
 * whatever light is actually falling on the floor, at night as well as noon,
 * and with the shadow map on or off.
 *
 *  - SKIRT: a band hugging the base of every structure that stands on a floor,
 *    dark where it meets the wall and fading out within ~1 m. Sides overlap at
 *    the corners on purpose: multiply is order-independent and two bands
 *    crossing is precisely where a real corner is darkest.
 *  - BLOT: hashed dirt/wear patches scattered over the open floor. These are
 *    what break the tile grid; without them the skirts just outline the grid.
 *
 * COST: quads, merged, static. One extra draw call for the whole map, no
 * per-frame work, no collision surface of any kind (they are 3 cm of paint).
 * `agent/bench-architecture.mjs` section H asserts the contract.
 * ========================================================================= */

/** One horizontal contact quad. `w` runs along the local U axis and `d` along
 *  local V BEFORE `rot` is applied about Y; the caller builds a plane of those
 *  extents, rotates it flat, then by `rot`. */
export interface ContactPatch {
  kind: "skirt" | "blot";
  w: number; d: number;
  x: number; y: number; z: number;
  /** Y rotation, radians. A skirt's is always a quarter turn. */
  rot: number;
  /** `grime` atlas cell: 0 = skirt ramp, 1..3 = the three dirt patches. */
  cell: number;
  /** Mirror U. Variation for free — a hashed flip doubles the apparent cells. */
  flip: boolean;
}

/** How far a skirt reaches out from the wall, at most. Also the cap on how far
 *  any quad here may stand outside a collider footprint — it is 3 cm of paint,
 *  the same class of exception as the road markings, and nothing else. */
export const SKIRT_MAX_REACH = 1.15;
/** Height above the floor surface. Above the road paint (2 cm) so a shadow
 *  falls ACROSS the lane markings rather than under them. */
export const CONTACT_LIFT = 0.035;
/** Max quads one structure's skirt can emit (4 sides x 8 segments). */
export const SKIRT_MAX_PIECES = 32;
/** Max blots one floor slab can emit. */
export const BLOT_MAX_PIECES = 30;
/** Longest a single skirt segment may be. Segments exist so a 40 m wall gets
 *  variation along its base instead of one texture stretched over the lot. */
export const SKIRT_SEG_M = 4.5;

/** Is this box something that should darken the floor it stands on? Small
 *  clutter is excluded for the same reason it is not dressed: a contact shadow
 *  under a 1 m crate is a smudge, and the budget buys a building instead. */
export function contactCaster(w: number, h: number, d: number): boolean {
  return h >= 2.2 && Math.max(w, d) >= 3;
}

/**
 * The floor surface a box is standing on, or `null` if it is standing on
 * nothing this module can see (a catwalk, a floating platform, a box stacked on
 * another box).
 *
 * This is the gate that keeps contact shadows OFF things that do not touch the
 * ground — the failure mode being a dark rectangle hanging in mid-air under a
 * sky bridge, which is worse than no shadow at all. A floor counts when its top
 * is within `TOL` of the box's underside and its footprint actually contains
 * the box centre.
 */
export function floorUnder(box: DressTarget, floors: readonly DressTarget[]): number | null {
  const bottom = box.p[1] - box.s[1] / 2;
  const cx = box.p[0], cz = box.p[2];
  const TOL = 0.75;
  let best: number | null = null;
  for (const f of floors) {
    const top = f.p[1] + f.s[1] / 2;
    if (top > bottom + TOL || top < bottom - TOL) continue;
    if (Math.abs(cx - f.p[0]) > f.s[0] / 2 + 0.2) continue;
    if (Math.abs(cz - f.p[2]) > f.s[2] / 2 + 0.2) continue;
    if (best === null || top > best) best = top;
  }
  return best;
}

/**
 * Emit the four skirt bands around one structure standing on a floor at
 * `floorTop`.
 *
 * Reach is keyed to HEIGHT — a tall building occludes far more of the sky above
 * the strip of floor beside it than a 3 m shed does, which is what an ambient
 * contact term physically is — and clamped so it can never become a ledge-sized
 * apron on a tower.
 */
export function contactSkirt(box: DressTarget, floorTop: number, out: ContactPatch[]): void {
  const [w, h, d] = box.s;
  const cx = box.p[0], cz = box.p[2];
  const reach = Math.min(SKIRT_MAX_REACH, Math.max(0.62, h * 0.11));
  const y = floorTop + CONTACT_LIFT;
  // The band runs the full face PLUS one reach at each end, so the two bands of
  // a corner cross over the corner itself instead of stopping short of it.
  const emit = (faceLen: number, rot: number, mx: number, mz: number, salt: number): void => {
    const span = faceLen + reach * 2;
    const n = Math.max(1, Math.min(8, Math.round(span / SKIRT_SEG_M)));
    const segW = span / n;
    for (let i = 0; i < n; i++) {
      const t = (i + 0.5) / n - 0.5;              // -0.5 .. +0.5 along the face
      // step along the face: for a band on a z face the face axis is x, and
      // vice versa. `rot` is the quarter turn that points the band's dark edge
      // at the wall, so the along-axis is derived from it rather than passed.
      const ax = Math.abs(Math.cos(rot)) > 0.5 ? 1 : 0;
      out.push({
        kind: "skirt",
        w: segW, d: reach,
        x: mx + (ax ? t * span : 0),
        y,
        z: mz + (ax ? 0 : t * span),
        rot,
        cell: 0,
        flip: hash01(mx + i * 3.7, mz + i * 5.1, salt) > 0.5,
      });
    }
  };
  // +z face: the wall lies toward -z of the band, which is where a quad's v=1
  // edge points at rot = 0 (PlaneGeometry rotated flat sends +V to -Z).
  emit(w, 0, cx, cz + d / 2 + reach / 2, 3);
  emit(w, Math.PI, cx, cz - d / 2 - reach / 2, 5);
  emit(d, Math.PI / 2, cx + w / 2 + reach / 2, cz, 7);
  emit(d, -Math.PI / 2, cx - w / 2 - reach / 2, cz, 11);
}

/**
 * Scatter dirt/wear blots over one floor slab.
 *
 * Deterministic (`hash01` off world position), inset from the slab edge so a
 * patch never hangs over a kerb, and area-proportional so a 12 m bay gets three
 * and a 90 m plaza gets fourteen rather than both getting the same handful.
 */
export function groundBlots(slab: DressTarget, out: ContactPatch[]): void {
  const [w, h, d] = slab.s;
  const cx = slab.p[0], cz = slab.p[2], top = slab.p[1] + h / 2;
  const n = Math.max(3, Math.min(BLOT_MAX_PIECES, Math.floor((w * d) / 42)));
  for (let i = 0; i < n; i++) {
    const hx = hash01(cx + i * 13.3, cz - i * 7.9, 31);
    const hz = hash01(cx - i * 9.1, cz + i * 11.7, 37);
    const hs = hash01(cx + i * 5.5, cz + i * 3.3, 41);
    const hr = hash01(cx - i * 4.2, cz - i * 8.8, 43);
    // size first: the inset depends on it, or a big blot on a small slab spills
    const size = 2.4 + hs * 4.2;
    const mx = Math.max(0, w / 2 - size / 2 - 0.5);
    const mz = Math.max(0, d / 2 - size / 2 - 0.5);
    out.push({
      kind: "blot",
      w: size, d: size * (0.72 + hr * 0.5),
      x: cx + (hx * 2 - 1) * mx,
      y: top + CONTACT_LIFT,
      z: cz + (hz * 2 - 1) * mz,
      rot: Math.floor(hr * 4) * (Math.PI / 2),
      cell: 1 + Math.floor(hs * 2.999),
      flip: hx > 0.5,
    });
  }
}

/** Max chips one structure emits. */
export const DEBRIS_MAX_BOXES = 6;
/** Tallest a chip may stand. Kerb/paint class — you cannot trip on it. */
export const DEBRIS_MAX_H = 0.13;

/**
 * Rubble at the base of a wall — the NON-texture half of the contact pass.
 *
 * A skirt darkens the joint; it does not give it any relief. A handful of
 * chips, spalled masonry and grit lying against the wall is what makes the
 * joint read as a place where two real surfaces meet, and unlike the skirt it
 * survives being looked at from two metres away. These merge into the existing
 * per-cell detail mesh, so they cost triangles and nothing else.
 */
export function baseDebris(box: DressTarget, floorTop: number, out: DressBox[]): void {
  const [w, h, d] = box.s;
  const cx = box.p[0], cz = box.p[2];
  for (let i = 0; i < DEBRIS_MAX_BOXES; i++) {
    const side = i % 4;
    const hA = hash01(cx + i * 17.3, cz + i * 6.1, 53);
    const hB = hash01(cx - i * 8.7, cz + i * 14.9, 59);
    const hC = hash01(cx + i * 3.1, cz - i * 19.3, 61);
    const cw = 0.13 + hA * 0.34, cd = 0.11 + hB * 0.3;
    const ch = Math.min(DEBRIS_MAX_H, 0.045 + hC * 0.085);
    // hugging the wall: the chip's outer edge never leaves the skirt's reach
    const off = 0.1 + hC * 0.28;
    const t = (hA * 2 - 1) * 0.42;
    const zFace = side < 2, sgn = side % 2 ? 1 : -1;
    out.push({
      w: cw, h: ch, d: cd,
      x: cx + (zFace ? t * w : sgn * (w / 2 + off)),
      y: floorTop + ch / 2,
      z: cz + (zFace ? sgn * (d / 2 + off) : t * d),
    });
  }
}

/* ===========================================================================
 * MINOR MASONRY (2026-10-23, `graphics`) — the boxes nothing else dresses.
 *
 * `dressStructure` stops at `dressable` (3.2 m tall, 4.5 m long). Everything
 * below that — the low walls, retaining walls, ledges, raised platforms, ship
 * rails and free-standing pillars that make up most of the COVER a player
 * actually fights around — was still a bare chamfered cuboid, front and centre
 * on every arena map (the standing "stacked cubes" lead in BACKLOG). A real
 * low wall has a COPING (a cap stone a few cm proud of both faces, which casts
 * the shadow line that says "built") and stands on a footing; a real pillar has
 * a base and a capital. That is all this adds.
 *
 * COLLISION CONTRACT — tighter than any other section in this file:
 *  - Nothing protrudes more than `MINOR_LIP` (6 cm) sideways. That is a drip
 *    edge, not a ledge: you cannot stand on it and it hides nothing.
 *  - Nothing rises more than `MINOR_RISE` (2.5 cm) above the collider top —
 *    road-paint/kerb class. A coping is a cap OVER the top face (so the
 *    collider's own top is hidden inside it, never coplanar with it).
 *  - The footing is only emitted when the caller found a floor under the box
 *    (`floorUnder`), so a floating beam never grows a plinth in mid-air.
 *  `agent/bench-architecture.mjs` section I asserts all of it on every map.
 *
 * COST. Max `MINOR_MAX_BOXES` 12-triangle boxes per target, merged into the
 * existing per-cell trim mesh: triangles and map-build time, never a draw call.
 * ========================================================================= */

/** Max boxes `dressMinor` emits for one target (4 coping + 4 footing). */
export const MINOR_MAX_BOXES = 8;
/** Max sideways protrusion past the collider, metres. */
export const MINOR_LIP = 0.06;
/** Max rise above the collider top, metres. */
export const MINOR_RISE = 0.025;

export type MinorKind = "wall" | "pillar";

/**
 * What, if anything, this undressed box is. `null` for crates and other
 * cube-ish clutter on purpose: a stone coping on a wooden crate is wrong, and a
 * crate's shape already reads as a crate.
 */
export function minorKind(w: number, h: number, d: number): MinorKind | null {
  if (![w, h, d].every((v) => Number.isFinite(v) && v > 0)) return null;
  if (dressable(w, h, d)) return null;               // dressStructure owns it
  const long = Math.max(w, d), short = Math.min(w, d);
  if (short < 0.2) return null;                      // a rail / trim strip
  // PILLAR: tall, slender, and roughly SQUARE in section. A 1.4 x 0.3 board
  // standing on end is a wall panel (the ship's hull is built from rows of
  // them) — a capital on each would stack into a lumpy band, so it is not one.
  if (h >= 2.4 && long <= 2.2 && short >= 0.3 && short >= long * 0.5 && h >= long * 1.6) return "pillar";
  // WALL / LEDGE: elongated, at least knee height, below the dressable bar.
  if (h >= 0.6 && h < 3.2 && long >= 2 && long >= short * 1.5) return "wall";
  return null;
}

/**
 * Emit the coping/capital (+ footing/base when `onFloor`) for one minor box.
 * Pure, deterministic, non-mutating.
 */
export function dressMinor(box: DressTarget, kind: MinorKind, onFloor: boolean, out: DressBox[]): void {
  const [w, h, d] = box.s;
  const x0 = box.p[0], y0 = box.p[1], z0 = box.p[2];
  const top = y0 + h / 2, bot = y0 - h / 2;
  const lip = MINOR_LIP;
  const put = (bw: number, bh: number, bd: number, x: number, y: number, z: number): void => {
    out.push({ w: bw, h: bh, d: bd, x, y, z });
  };

  if (kind === "pillar") {
    // CAPITAL — one block enclosing the pillar head; BASE — one enclosing its foot.
    const capH = Math.min(0.22, Math.max(0.1, h * 0.05));
    put(w + lip * 2, capH, d + lip * 2, x0, top + MINOR_RISE - capH / 2, z0);
    if (onFloor) {
      const baseH = Math.min(0.3, Math.max(0.12, h * 0.07));
      put(w + lip * 2, baseH, d + lip * 2, x0, bot + baseH / 2, z0);
    }
    return;
  }

  // COPING. Thin walls get one cap stone; a wide ledge/platform gets a RING
  // band round its edge so its walkable top keeps its own surface material.
  const capH = Math.min(0.14, Math.max(0.06, h * 0.07));
  const cy = top + MINOR_RISE - capH / 2;
  const short = Math.min(w, d);
  if (short <= 0.9) {
    put(w + lip * 2, capH, d + lip * 2, x0, cy, z0);
  } else {
    const band = Math.min(0.32, short * 0.25);
    // X-running pieces span the full length (corners included); Z-running
    // pieces fill BETWEEN them, so no two ring pieces overlap.
    const bd = band + lip;
    put(w + lip * 2, capH, bd, x0, cy, z0 - d / 2 - lip + bd / 2);
    put(w + lip * 2, capH, bd, x0, cy, z0 + d / 2 + lip - bd / 2);
    const bw = band + lip, inner = d - band * 2;
    put(bw, capH, inner, x0 - w / 2 - lip + bw / 2, cy, z0);
    put(bw, capH, inner, x0 + w / 2 + lip - bw / 2, cy, z0);
  }

  // FOOTING — a short band hugging the base, only on a box standing on a floor
  // and tall enough that a footing is a detail rather than half the wall.
  if (onFloor && h >= 0.9) {
    const fH = Math.min(0.16, h * 0.1);
    const fl = lip * 0.75;
    const fy = bot + fH / 2;
    // same embed-by-60% shape as the structure plinth: thickness `fl`, centred
    // 0.4·fl outside the face, so the outer face sits 0.9·fl proud.
    put(w + fl * 2, fH, fl, x0, fy, z0 - d / 2 - fl * 0.4);
    put(w + fl * 2, fH, fl, x0, fy, z0 + d / 2 + fl * 0.4);
    put(fl, fH, d, x0 - w / 2 - fl * 0.4, fy, z0);
    put(fl, fH, d, x0 + w / 2 + fl * 0.4, fy, z0);
  }
}

/* =========================================================================
 * HOUSE WINDOWS (2026-11-03, realism pass). The hollow houses (village,
 * compound, Skyhaven…) are shells of 0.3-0.4 m slabs, and a thin wall never
 * qualifies for the facade pass (`facadeLike` wants ≥ 0.9 m) nor, off the
 * painted-street maps, for a shopfront — so every house in the village was a
 * blank stone box. This emits real JOINERY on the outward face of each such
 * wall, one window per bay per storey: a timber frame (head, cill, jambs, a
 * cross mullion/transom), a projecting stone cill, a glass pane and — on the
 * rustic day maps — a pair of open shutters.
 *
 * COLLISION CONTRACT: everything is on the OUTWARD face and stands < 0.17 m
 * proud of it (cill 0.16, shutters 0.07), under the file's 0.25 m ledge
 * allowance. The glass is a plane 0.025 m proud — the wall is one solid
 * collider, so a recessed pane would be hidden by the face (same trade as the
 * shopfront). Nothing touches the inward face: inside a house you still see
 * the plain wall, which is what the collider is.
 * ========================================================================= */

export interface WindowPiece {
  kind: "frame" | "cill" | "glass" | "shutter";
  /** full extents; `glass` has a ZERO extent on its facing axis (it is a plane) */
  w: number; h: number; d: number;
  x: number; y: number; z: number;
  /** outward normal (axis-aligned unit) */
  nx: number; nz: number;
  /** stable per-house hash in [0,1) — shutter colourway, curtain choice */
  tone: number;
}

export const WIN_STORY = 3;
export const WIN_W = 0.95;
export const WIN_H = 1.3;
export const WIN_SILL = 0.95;   // cill height above the storey floor
export const WIN_BAY = 4.0;     // target spacing between window centres (cottage rhythm)
export const WIN_MAX_PIECES = 120;

/** Maps whose thin walls are NOT house shells: a ship's hull planking and a
 *  castle's curtain walls would wear cottage windows. */
const NO_HOUSE_WINDOWS = new Set(["pirate_ship", "medieval_castle"]);
export function houseWindowMap(mapId: string | undefined): boolean {
  return !mapId || !NO_HOUSE_WINDOWS.has(mapId);
}

/** A thin shell wall worth glazing: occupied-storey height, a bay wide. */
export function windowable(w: number, h: number, d: number): boolean {
  const t = Math.min(w, d), len = Math.max(w, d);
  return t >= 0.15 && t < 0.9 && h >= 2.6 && len >= 2.4;
}

/**
 * Emit windows for one thin wall. `outward` is `shellOutward`'s ±1 along the
 * wall's thin axis (0 = unknown → nothing: guessing would glaze the inside of
 * a room). `shutters` adds the open shutter pair. `fromStorey` = 1 on a wall
 * whose ground storey already carries a shopfront.
 */
export function houseWindows(
  box: DressTarget, outward: number, shutters: boolean, out: WindowPiece[], fromStorey = 0,
): void {
  const [w, h, d] = box.s;
  if (!outward || !windowable(w, h, d)) return;
  const faceX = w < d;                     // thin along X → the face looks along ±X
  const len = faceX ? d : w;
  const half = (faceX ? w : d) / 2;
  const nx = faceX ? outward : 0, nz = faceX ? 0 : outward;
  const cx = box.p[0], cz = box.p[2];
  const base = box.p[1] - h / 2, top = box.p[1] + h / 2;
  // ONE tone per HOUSE, not per wall: hash a point ~3 m inside the shell, on
  // an 8 m grid, so the four walls of a 9 m house agree on their shutters
  const tone = hash01(Math.round((cx - nx * 3) / 8), Math.round((cz - nz * 3) / 8), 7.7);
  const F = 0.075;  // frame member width
  const FP = 0.07;  // frame depth proud of the face
  const SW = WIN_W / 2 + 0.02; // one shutter leaf
  // bays: centred; the OUTER edge of each window (shutters included) stays
  // 0.25 m clear of the wall end so a corner reads clean
  const halfOuter = WIN_W / 2 + (shutters ? F + SW : F / 2);
  const usable = len - 0.5 - 2 * halfOuter;
  if (usable < 0) return;
  const n = Math.floor(usable / WIN_BAY) + 1;
  const pitch = n > 1 ? Math.min(WIN_BAY, usable / (n - 1)) : 0;
  const along0 = -(pitch * (n - 1)) / 2;
  const put = (kind: WindowPiece["kind"], a: number, high: number, along: number, y: number, proud: number): void => {
    // `proud`: depth the piece stands out of the face; centred at half that
    const off = half + proud / 2;
    const px = faceX ? cx + nx * off : cx + along;
    const pz = faceX ? cz + along : cz + nz * off;
    const depth = kind === "glass" ? 0 : proud;
    out.push({
      kind, w: faceX ? depth : a, h: high, d: faceX ? a : depth,
      x: kind === "glass" && faceX ? cx + nx * (half + 0.025) : px,
      y, z: kind === "glass" && !faceX ? cz + nz * (half + 0.025) : pz,
      nx, nz, tone,
    });
  };
  for (let s = fromStorey; ; s++) {
    const sill = base + s * WIN_STORY + WIN_SILL;
    const head = sill + WIN_H;
    if (head > top - 0.3) break;
    const yc = (sill + head) / 2;
    for (let i = 0; i < n; i++) {
      const a = along0 + i * pitch;
      put("glass", WIN_W - F, WIN_H - F, a, yc, 0);
      put("frame", WIN_W + F, F, a, head + F / 2 - F, FP);            // head
      put("frame", WIN_W + F, F, a, sill + F / 2, FP);                 // bottom rail
      put("frame", F, WIN_H, a - WIN_W / 2, yc, FP);                   // jambs
      put("frame", F, WIN_H, a + WIN_W / 2, yc, FP);
      put("frame", F * 0.6, WIN_H - F, a, yc, FP * 0.8);               // mullion
      put("frame", WIN_W - F, F * 0.6, a, sill + WIN_H * 0.62, FP * 0.8); // transom
      put("cill", WIN_W + 0.22, 0.07, a, sill - 0.035, 0.16);          // stone cill
      if (shutters) {
        // open, folded flat to the wall either side of the opening
        put("shutter", SW, WIN_H + 0.04, a - WIN_W / 2 - F - SW / 2, yc, 0.045);
        put("shutter", SW, WIN_H + 0.04, a + WIN_W / 2 + F + SW / 2, yc, 0.045);
      }
      if (out.length > WIN_MAX_PIECES) return;
    }
  }
}
